import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { Label, Grid, SectionHeader, PageHeader, css, Toast, MontoInput } from '../components/UI';
import { Icono, ConIcono } from '../components/Iconos';
import { validarCLABE, hoyISO } from '../utils';
import { Modal } from '../components/UI';
import { SelectorCuentaProveedor, cuentasDelProveedor, claveCuenta, POR_CONFIRMAR } from '../components/CuentasProveedor';
import { parseCFDI, leerArchivo, datosFiscales, soloFiscales, desglosarImpuestos, netoConImpuestos, IMPUESTO_LABEL } from '../cfdi';


// ¿Este CFDI ya está en otro concepto de la misma solicitud? Devuelve el aviso o
// null. Sin esto, el mismo comprobante descargado dos veces ("ticket (1).xml") se
// captura como dos gastos y el importe se cobra doble: pasó en una caja chica de
// siete tickets donde dos eran el mismo comprobante. El flujo de facturas normales
// ya avisaba de los repetidos; el de conceptos no validaba nada.
function cfdiYaUsado(conceptos, idx, cfdi) {
  const folio = (cfdi.folioFiscal || cfdi.referencia || '').trim();
  if (!folio) return null; // sin timbrar no hay folio fiscal con el que comparar
  const otro = conceptos.findIndex((c, i) => i !== idx && (c.folioCfdi || '').trim() === folio);
  if (otro < 0) return null;
  const cual = conceptos[otro].motivo ? ` (${conceptos[otro].motivo})` : '';
  return `Ese CFDI ya está en el concepto ${otro + 1}${cual}: es el mismo comprobante. `
    + 'Si lo capturas dos veces, el gasto se cobra doble.';
}

const IMPUESTOS_DEF = [
  { clave: 'IVA_16',        label: 'IVA 16%',              rate: 0.16,      esRetencion: false },
  { clave: 'RET_IVA_10667', label: 'IVA RETENCIÓN 10.67%', rate: 0.106667,  esRetencion: true  },
  { clave: 'RET_IVA_16',    label: 'IVA RETENCIÓN 16%',    rate: 0.16,      esRetencion: true  },
  { clave: 'RET_ISR_125', label: 'RETENCIÓN ISR 1.25%',  rate: 0.0125,  esRetencion: true  },
  { clave: 'RET_ISR_10',  label: 'ISR RETENCIÓN 10%',    rate: 0.10,    esRetencion: true  },
  { clave: 'RET_4',       label: 'RETENCIÓN 4%',         rate: 0.04,    esRetencion: true  },
  { clave: 'RET_IEPS',    label: 'RETENCIÓN IEPS 26.5%', rate: 0.265,   esRetencion: true  },
  { clave: 'IEPS_TRAS',   label: 'IEPS TRASLADADO',      rate: 0,       esRetencion: false },
];

function calcImpuestos(monto, claves = [], importes = null) {
  const base = parseFloat(monto) || 0;
  let iva = 0, retenciones = 0;
  const lineas = [];
  for (const d of IMPUESTOS_DEF) {
    if (!claves.includes(d.clave)) continue;
    const val = importes && importes[d.clave] != null ? importes[d.clave] : base * d.rate;
    lineas.push({ ...d, valor: val });
    if (!d.esRetencion) iva += val; else retenciones += val;
  }
  return { lineas, total: Math.round((base + iva - retenciones) * 100) / 100 };
}

function MotivoSearch({ value, onChange, hasError, motivos }) {
  // Solo el catálogo real: sin lista de respaldo. Ofrecer motivos que no existen
  // en la base deja la solicitud sin cuenta contable ni agrupadora (no hay contra
  // qué resolverlas) y el PDF sale vacío.
  const lista = motivos || [];
  const sinCatalogo = lista.length === 0;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(''); // filtro de búsqueda dentro del desplegable
  const wrap = useRef(null);
  const inputBusqueda = useRef(null);

  useEffect(() => {
    const h = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Al abrir: limpia el filtro y pone el cursor listo para escribir
  useEffect(() => {
    if (open) { setQ(''); setTimeout(() => inputBusqueda.current?.focus(), 0); }
  }, [open]);

  const texto = q.trim().toLowerCase();
  const filtrados = texto ? lista.filter(m => m.toLowerCase().includes(texto)) : lista;

  const base = css.input(hasError);

  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      {/* Solo selección: se elige de la lista, pero se puede filtrar escribiendo */}
      <div
        onClick={() => { if (!sinCatalogo) setOpen(o => !o); }}
        style={{
          ...base,
          cursor: sinCatalogo ? 'not-allowed' : 'pointer', userSelect: 'none',
          opacity: sinCatalogo ? 0.6 : 1,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          color: value ? 'var(--ink)' : 'var(--glass)',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || (sinCatalogo ? 'Sin motivos configurados' : 'Selecciona el motivo de pago...')}
        </span>
        <span style={{ fontSize: 11.5, color: 'var(--glass)', flexShrink: 0, transition: 'transform 0.18s', transform: open ? 'rotate(180deg)' : 'none' }}>▼</span>
      </div>
      {sinCatalogo && (
        <div style={{ fontSize: 11.5, color: 'var(--st-pendiente)', marginTop: 3, lineHeight: 1.35 }}>
          <Icono nombre="alerta" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />No hay motivos configurados para este tipo de solicitud. Pídele al administrador
          que los cargue (Admin → catálogo de motivos) para que la solicitud quede con su
          cuenta contable y cuenta agrupadora.
        </div>
      )}
      {open && lista.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
          background: 'var(--panel)', border: '1px solid var(--hair)', borderRadius: 10,
          boxShadow: '0 12px 32px rgba(0,0,0,0.12)', marginTop: 4, overflow: 'hidden',
        }}>
          {/* Buscador fijo arriba de la lista */}
          <div style={{ padding: 8, borderBottom: '1px solid var(--hair)', background: 'var(--hair-soft)' }}>
            <input
              ref={inputBusqueda}
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && filtrados.length === 1) { onChange(filtrados[0]); setOpen(false); } if (e.key === 'Escape') setOpen(false); }}
              placeholder={`Buscar entre ${lista.length} motivos...`}
              style={{ ...css.input(false), fontSize: 12.5, padding: '8px 11px' }}
            />
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
          {filtrados.length === 0 && (
            <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--glass)' }}>Sin coincidencias para "{q}".</div>
          )}
          {filtrados.map((m, i) => {
            const sel = m === value;
            return (
              <div key={m} onMouseDown={() => { onChange(m); setOpen(false); }}
                style={{
                  padding: '9px 14px', cursor: 'pointer', fontSize: 13,
                  borderBottom: i < filtrados.length - 1 ? '1px solid var(--hair-soft)' : 'none',
                  color: sel ? 'var(--plasma)' : 'var(--ink-soft)',
                  background: sel ? 'var(--plasma-dim)' : 'transparent',
                  fontWeight: sel ? 600 : 400,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--plasma-dim)'; e.currentTarget.style.color = 'var(--plasma)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = sel ? 'var(--plasma-dim)' : 'transparent'; e.currentTarget.style.color = sel ? 'var(--plasma)' : 'var(--ink-soft)'; }}>
                {m}
              </div>
            );
          })}
          </div>
        </div>
      )}
    </div>
  );
}

function ProveedorSearch({ value, onSelect, endpoint = '/proveedores' }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const wrap = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try { const d = await api.get(`${endpoint}?q=${encodeURIComponent(q)}`); setResults(d.items || []); }
    catch { setResults([]); }
    finally { setLoading(false); }
  }, [endpoint]);

  useEffect(() => { clearTimeout(timer.current); timer.current = setTimeout(() => search(query), 280); return () => clearTimeout(timer.current); }, [query, search]);
  useEffect(() => {
    const h = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      {/* Solo permite seleccionar del catálogo: escribir aquí filtra resultados, nunca fija el valor por sí solo */}
      <input value={query} placeholder="Escribe para buscar proveedor en el catálogo..."
        onChange={e => { const v = e.target.value; setQuery(v); setOpen(true); if (value) onSelect(null); }}
        onFocus={() => setOpen(true)}
        style={css.input(false)}
      />
      {open && (results.length > 0 || loading) && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999, background: 'var(--panel)', border: '1px solid var(--mesh)', borderRadius: 10, boxShadow: '0 12px 32px rgba(15,23,42,0.14)', maxHeight: 260, overflowY: 'auto', marginTop: 4 }}>
          {loading && <div style={{ padding: '10px 14px', color: 'var(--glass)', fontSize: 13 }}>Buscando...</div>}
          {results.map((p, i) => (
            <div key={p._id || i} onMouseDown={() => { onSelect(p); setOpen(false); }}
              style={{ padding: '9px 14px', cursor: 'pointer', borderBottom: i < results.length - 1 ? '1px solid var(--hair-soft)' : 'none' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--plasma-dim)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{p.nombre}</div>
              {p.banco && <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 1 }}>{p.banco} · {p.clabe || '—'}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FacturasTable({ facturas, onChange, hasError, rfcProveedor }) {
  const upd = (idx, field, val) =>
    onChange(facturas.map((f, i) => i === idx ? { ...f, [field]: val } : f));
  const add = () =>
    onChange([...facturas, { folioCfdi: '', notasCredito: '', monto: '' }]);
  const remove = (idx) => {
    if (facturas.length <= 1) return;
    onChange(facturas.filter((_, i) => i !== idx));
  };
  const fmt = n => `$${(parseFloat(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(90px, 140px) 34px', gap: 6, marginBottom: 4 }}>
        {['Folio CFDI', 'Notas de Crédito', 'Base ($)', ''].map((h, i) => (
          <div key={i} style={{ fontSize: 12, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</div>
        ))}
      </div>
      {facturas.map((f, idx) => {
        const montoErr = hasError && (!f.monto || isNaN(parseFloat(f.monto)) || parseFloat(f.monto) <= 0);
        const fromXml = !!f._xml;
        return (
          <div key={idx} style={{ marginBottom: 6 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(90px, 140px) 34px', gap: 6 }}>
              <input value={f.folioCfdi} onChange={e => upd(idx, 'folioCfdi', e.target.value)}
                placeholder={`CFDI-${String(idx + 1).padStart(3, '0')}`}
                title={f.folioCfdi}
                style={{ ...css.input(!!f._dup), ...(fromXml ? { fontFamily: 'var(--digit-font)', fontSize: 11.5 } : {}) }} />
              <input value={f.notasCredito} onChange={e => upd(idx, 'notasCredito', e.target.value)}
                placeholder="NC-001" style={css.input(false)} />
              <MontoInput value={f.monto} onChange={v => upd(idx, 'monto', v)} err={montoErr} />
              <button aria-label="Quitar factura" title="Quitar factura" onClick={() => remove(idx)} disabled={facturas.length <= 1} style={{
                borderRadius: 7, border: '1px solid var(--danger-line)', background: 'var(--danger-bg)',
                color: 'var(--danger)', cursor: facturas.length <= 1 ? 'not-allowed' : 'pointer',
                fontSize: 14, opacity: facturas.length <= 1 ? 0.3 : 1, fontFamily: 'Inter, sans-serif',
              }}><Icono nombre="papelera" size={15} /></button>
            </div>
            {fromXml && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '3px 2px 0', fontSize: 11.5 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: 'var(--ok)', fontWeight: 700 }}><Icono nombre="codigo" size={13} />XML</span>
                {f._rfc && <span style={{ color: 'var(--glass)' }}>RFC <b style={{ color: 'var(--ink-soft)' }}>{f._rfc}</b></span>}
                {f._emisor && <span style={{ color: 'var(--glass)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f._emisor}</span>}
                {f._total != null && <span style={{ color: 'var(--glass)' }}>Total CFDI <b style={{ color: 'var(--ink)', fontFamily: 'var(--digit-font)' }}>{fmt(f._total)}</b></span>}
                {f._egreso && <span style={{ color: 'var(--danger)', fontWeight: 700 }}>Nota de crédito (se resta)</span>}
                {rfcProveedor && f._rfc && f._rfc.toUpperCase() !== rfcProveedor.toUpperCase() && (
                  <span style={{ color: 'var(--danger)', fontWeight: 700 }}><Icono nombre="alerta" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />No corresponde al proveedor ({rfcProveedor})</span>
                )}
                {f._dup && <span style={{ color: 'var(--danger)', fontWeight: 700 }}><Icono nombre="alerta" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />{f._dup === 'local' ? 'Repetida en esta lista' : `Ya existe en ${f._dup}`}</span>}
                {f._sinTimbrar && (
                  <span title="El XML no trae timbre fiscal (UUID, sellos ni fecha de timbrado). No ampara el gasto ni permite acreditar el IVA."
                    style={{ color: 'var(--st-pendiente)', fontWeight: 700 }}><Icono nombre="alerta" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />Sin timbrar — pide el CFDI timbrado</span>
                )}
              </div>
            )}
          </div>
        );
      })}
      <button onClick={add} style={{
        padding: '5px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600,
        border: '1.5px dashed var(--hair)', background: 'var(--hair-soft)', color: 'var(--glass)',
        cursor: 'pointer', marginTop: 2, fontFamily: 'Inter, sans-serif',
      }}>+ Agregar factura</button>
      {hasError && <div style={{ color: 'var(--danger)', fontSize: 11.5, marginTop: 5 }}>Ingresa un monto válido en cada factura</div>}
    </div>
  );
}

// Config por tipo de solicitud: catálogo de motivos, título e ícono.
const TIPO_CFG = {
  pago:           { titulo: 'Nueva Solicitud de Pago',           icon: 'editar', motivos: '/motivos' },
  debito:         { titulo: 'Nueva Solicitud de Débito',         icon: 'banco', motivos: '/motivos-debito' },
  contribuciones: { titulo: 'Nueva Solicitud de Contribuciones', icon: 'edificio', motivos: '/motivos-contribuciones' },
  activos_fijos:  { titulo: 'Nueva Solicitud de Activos Fijos',  icon: 'edificio', motivos: '/motivos-activos-fijos' },
  mercancias_nacionales: { titulo: 'Nueva Adquisición de Mercancías Nacionales', icon: 'caja', motivos: '/motivos-mercancias-nacionales' },
  nomina:         { titulo: 'Nueva Solicitud de Pago de Nómina', icon: 'personas', motivos: '/motivos-nomina' },
  caja_chica:     { titulo: 'Nueva Comprobación Caja Chica',    icon: 'calculadora', motivos: '/motivos' },
  reembolso_gastos: { titulo: 'Nueva Comprobación Reembolso de Gastos', icon: 'billete', motivos: '/motivos' },
  // Único tipo con catálogo de proveedores propio: agentes aduanales, navieras y
  // transportistas no tienen por qué ensuciar el buscador del gasto normal.
  complemento_importacion: { titulo: 'Nuevo Complemento a Costo de Importación', icon: 'barco', motivos: '/motivos-complemento-importacion', proveedores: '/proveedores-importacion' },
};

// Editor de conceptos de nómina: varios motivos, cada uno con monto y si suma o resta al neto.
function ConceptosNomina({ conceptos, onChange, motivos, hasError }) {
  const upd = (i, field, val) => onChange(conceptos.map((c, idx) => idx === i ? { ...c, [field]: val } : c));
  const add = () => onChange([...conceptos, { motivo: '', monto: '', operacion: 'suma' }]);
  const remove = (i) => { if (conceptos.length <= 1) return; onChange(conceptos.filter((_, idx) => idx !== i)); };
  const total = conceptos.reduce((s, c) => s + (parseFloat(c.monto) || 0) * (c.operacion === 'resta' ? -1 : 1), 0);
  const fmt = n => `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
  const COLS = 'minmax(84px, 116px) minmax(0, 1fr) minmax(90px, 150px) 34px';
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 6, marginBottom: 4 }}>
        {['Operación', 'Motivo de pago', 'Monto ($)', ''].map((h, i) => (
          <div key={i} style={{ fontSize: 12, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</div>
        ))}
      </div>
      {conceptos.map((c, i) => {
        const montoErr  = hasError && (!c.monto || isNaN(parseFloat(c.monto)) || parseFloat(c.monto) <= 0);
        const motivoErr = hasError && !c.motivo?.trim();
        const resta = c.operacion === 'resta';
        return (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: COLS, gap: 6, marginBottom: 6, alignItems: 'start' }}>
            <button onClick={() => upd(i, 'operacion', resta ? 'suma' : 'resta')} title="Alterna entre sumar y restar este concepto" style={{
              padding: '9px 6px', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
              border: `1.5px solid ${resta ? 'var(--danger-line)' : 'var(--ok-line)'}`,
              background: resta ? 'var(--danger-bg)' : 'var(--ok-bg)',
              color: resta ? 'var(--danger)' : 'var(--ok)',
            }}>{resta ? 'Resta' : 'Suma'}</button>
            <MotivoSearch value={c.motivo} onChange={v => upd(i, 'motivo', v)} hasError={motivoErr} motivos={motivos} />
            <MontoInput value={c.monto} onChange={v => upd(i, 'monto', v)} err={montoErr} />
            <button aria-label="Quitar concepto" title="Quitar concepto" onClick={() => remove(i)} disabled={conceptos.length <= 1} style={{
              borderRadius: 7, border: '1px solid var(--danger-line)', background: 'var(--danger-bg)',
              color: 'var(--danger)', cursor: conceptos.length <= 1 ? 'not-allowed' : 'pointer',
              fontSize: 14, opacity: conceptos.length <= 1 ? 0.3 : 1, fontFamily: 'Inter, sans-serif',
            }}><Icono nombre="papelera" size={15} /></button>
          </div>
        );
      })}
      <button onClick={add} style={{
        padding: '5px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600,
        border: '1.5px dashed var(--hair)', background: 'var(--hair-soft)', color: 'var(--glass)',
        cursor: 'pointer', marginTop: 2, fontFamily: 'Inter, sans-serif',
      }}>+ Agregar motivo</button>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10, alignItems: 'baseline' }}>
        <span style={{ fontSize: 11.5, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Neto a Pagar</span>
        <span style={{ fontSize: 17, fontWeight: 700, color: total < 0 ? 'var(--danger)' : 'var(--plasma)', fontFamily: 'var(--digit-font)' }}>{fmt(total)}</span>
      </div>
      {hasError && <div style={{ color: 'var(--danger)', fontSize: 11.5, marginTop: 5 }}>Cada concepto necesita un motivo y un monto válido</div>}
    </div>
  );
}

// Editor de un desglose por motivo: cada renglón es un motivo del catálogo del tipo
// (→ su cuenta contable) con su importe, y el total es la suma. Sin columna de
// operación: a diferencia de la nómina, aquí nada resta.
// Lo usan el complemento a costo de importación —que además adjunta una factura por
// renglón— y las contribuciones, que sólo llevan motivo y monto.
// `impuestosCfdi` son los importes REALES del XML por código. El neto se calcula con
// la misma regla que el backend (traslados suman, retenciones restan): antes este
// previo hacía base + IVA y se comía las retenciones, así que una factura con
// retención de ISR/IVA —honorarios, fletes— mostraba de más y no cuadraba con el CFDI.
function ConceptosPorMotivo({ conceptos, onChange, motivos, hasError, impuestosCfdi = {}, onImportar, importando, onImportarConcepto, importandoIdx, etiquetaTotal = 'Neto a pagar' }) {
  const upd = (i, field, val) => onChange(conceptos.map((c, idx) => idx === i ? { ...c, [field]: val } : c));
  const add = () => onChange([...conceptos, { motivo: '', monto: '' }]);
  const remove = (i) => { if (conceptos.length <= 1) return; onChange(conceptos.filter((_, idx) => idx !== i)); };
  // Quitar el CFDI de un renglón: se van sus impuestos con él, para que el neto
  // no siga contando una factura que ya no está.
  const quitarCfdi = (i) => onChange(conceptos.map((c, idx) => idx !== i ? c : {
    motivo: c.motivo, monto: c.monto, _descripcion: c._descripcion,
  }));
  const total = conceptos.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
  const fmt = n => `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
  const COLS = 'minmax(0, 1fr) minmax(90px, 150px) 34px';
  // Qué impuestos cuentan: si algún renglón trae su propia factura mandan los de los
  // renglones (una por proveedor); si no, el CFDI único importado a nivel solicitud.
  // Nunca se mezclan, para no contar dos veces la misma factura.
  const porConcepto = conceptos.some(c => Object.keys(c._impuestosImportes || {}).length);
  const impuestosEfectivos = porConcepto
    ? conceptos.reduce((acc, c) => {
        for (const [k, v] of Object.entries(c._impuestosImportes || {})) {
          acc[k] = Math.round(((acc[k] || 0) + (Number(v) || 0)) * 100) / 100;
        }
        return acc;
      }, {})
    : impuestosCfdi;
  const { traslados, retenciones } = desglosarImpuestos(impuestosEfectivos);
  const neto = netoConImpuestos(total, impuestosEfectivos);
  const detalleRet = Object.entries(impuestosEfectivos)
    .filter(([k]) => k.startsWith('RET'))
    .map(([k, v]) => `${IMPUESTO_LABEL[k] || k}: ${fmt(Number(v) || 0)}`).join('\n');
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11.5, color: 'var(--glass)' }}>Cada concepto se clasifica en su propia cuenta contable (según su motivo).</span>
        {onImportar && (
          <button onClick={onImportar} disabled={importando} style={{ ...css.btn('secundario'), padding: '4px 12px', fontSize: 11.5, cursor: importando ? 'wait' : 'pointer' }} title="Lee el XML del agente aduanal: prellena un renglón por concepto y toma el IVA real del CFDI">
            {importando ? 'Leyendo...' : <ConIcono nombre="codigo" size={13}>Importar XML (auto-llenar)</ConIcono>}
          </button>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 6, marginBottom: 4 }}>
        {['Motivo de pago', 'Monto ($)', ''].map((h, i) => (
          <div key={i} style={{ fontSize: 12, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</div>
        ))}
      </div>
      {conceptos.map((c, i) => {
        const montoErr  = hasError && (!c.monto || isNaN(parseFloat(c.monto)) || parseFloat(c.monto) <= 0);
        const motivoErr = hasError && !c.motivo?.trim();
        return (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: COLS, gap: 6, marginBottom: 6, alignItems: 'start' }}>
            <div>
              <MotivoSearch value={c.motivo} onChange={v => upd(i, 'motivo', v)} hasError={motivoErr} motivos={motivos} />
              {c._descripcion && (
                <div title={c._descripcion} style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  En la factura: {c._descripcion}
                </div>
              )}
              {/* Factura propia del renglón: cada gasto de la importación suele venir
                  de un proveedor distinto, así que cada uno puede traer su CFDI. */}
              {onImportarConcepto && (
                c.folioCfdi ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                    <span title={`${c.nombreEmisor || ''}\nUUID ${c.folioCfdi}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 5, fontSize: 11.5, fontWeight: 600, background: 'var(--ok-bg)', color: 'var(--ok)', border: '1px solid var(--ok-line)', maxWidth: 260 }}>
                      <Icono nombre="codigo" size={13} style={{ flexShrink: 0 }} /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nombreEmisor || c.rfcEmisor || 'CFDI'}</span>
                    </span>
                    <button aria-label="Quitar el CFDI de este concepto" onClick={() => quitarCfdi(i)} title="Quitar el CFDI de este concepto"
                      style={{ border: 'none', background: 'transparent', color: 'var(--danger)', cursor: 'pointer', fontSize: 11.5, padding: 0 }}><Icono nombre="cerrar" size={13} /></button>
                  </div>
                ) : (
                  <button onClick={() => onImportarConcepto(i)} disabled={importandoIdx === i}
                    title="Adjuntar el CFDI de ESTE concepto: toma sus impuestos reales"
                    style={{ marginTop: 4, padding: '2px 9px', borderRadius: 5, fontSize: 11.5, fontWeight: 600, border: '1px dashed var(--ok-line)', background: 'transparent', color: 'var(--ok)', cursor: importandoIdx === i ? 'wait' : 'pointer', fontFamily: 'Inter, sans-serif' }}>
                    {importandoIdx === i ? 'Leyendo...' : <ConIcono nombre="clip" size={13}>Adjuntar XML de este concepto</ConIcono>}
                  </button>
                )
              )}
            </div>
            <MontoInput value={c.monto} onChange={v => upd(i, 'monto', v)} err={montoErr} />
            <button aria-label="Quitar concepto" title="Quitar concepto" onClick={() => remove(i)} disabled={conceptos.length <= 1} style={{
              borderRadius: 7, border: '1px solid var(--danger-line)', background: 'var(--danger-bg)',
              color: 'var(--danger)', cursor: conceptos.length <= 1 ? 'not-allowed' : 'pointer',
              fontSize: 14, opacity: conceptos.length <= 1 ? 0.3 : 1, fontFamily: 'Inter, sans-serif',
            }}><Icono nombre="papelera" size={15} /></button>
          </div>
        );
      })}
      <button onClick={add} style={{
        padding: '5px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600,
        border: '1.5px dashed var(--hair)', background: 'var(--hair-soft)', color: 'var(--glass)',
        cursor: 'pointer', marginTop: 2, fontFamily: 'Inter, sans-serif',
      }}>+ Agregar motivo</button>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, marginTop: 10, fontSize: 12.5 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
          <span style={{ fontSize: 11.5, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Suma de conceptos</span>
          <span style={{ fontFamily: 'var(--digit-font)', fontWeight: 600, minWidth: 110, textAlign: 'right' }}>{fmt(total)}</span>
        </div>
        {traslados > 0 && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
            <span style={{ fontSize: 11.5, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{porConcepto ? 'IVA de las facturas' : 'IVA del CFDI'}</span>
            <span style={{ fontFamily: 'var(--digit-font)', fontWeight: 600, minWidth: 110, textAlign: 'right' }}>{fmt(traslados)}</span>
          </div>
        )}
        {retenciones > 0 && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }} title={detalleRet}>
            <span style={{ fontSize: 11.5, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{porConcepto ? 'Retenciones de las facturas' : 'Retenciones del CFDI'}</span>
            <span style={{ fontFamily: 'var(--digit-font)', fontWeight: 600, minWidth: 110, textAlign: 'right', color: 'var(--danger)' }}>−{fmt(retenciones)}</span>
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
          <span style={{ fontSize: 11.5, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{etiquetaTotal}</span>
          <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--plasma)', fontFamily: 'var(--digit-font)', minWidth: 110, textAlign: 'right' }}>{fmt(neto)}</span>
        </div>
      </div>
      {hasError && <div style={{ color: 'var(--danger)', fontSize: 11.5, marginTop: 5 }}>Cada concepto necesita un motivo y un monto válido</div>}
    </div>
  );
}

// Editor de conceptos de débito: cada renglón tiene su motivo (→ cuenta contable),
// monto base e IVA. Se puede auto-llenar desde el XML (ej. SI VALE: comisión + fondo).
function ConceptosDebito({ conceptos, onChange, motivos, hasError, onImportar, importando }) {
  const upd = (i, field, val) => onChange(conceptos.map((c, idx) => idx === i ? { ...c, [field]: val } : c));
  const add = () => onChange([...conceptos, { motivo: '', monto: '', iva: '' }]);
  const remove = (i) => { if (conceptos.length <= 1) return; onChange(conceptos.filter((_, idx) => idx !== i)); };
  const base = conceptos.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
  const iva  = conceptos.reduce((s, c) => s + (parseFloat(c.iva) || 0), 0);
  const fmt = n => `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
  const COLS = 'minmax(0, 1fr) minmax(80px, 130px) minmax(80px, 110px) 34px';
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11.5, color: 'var(--glass)' }}>Cada concepto se clasifica en su propia cuenta contable (según su motivo).</span>
        <button onClick={onImportar} disabled={importando} style={{ ...css.btn('secundario'), padding: '4px 12px', fontSize: 11.5, cursor: importando ? 'wait' : 'pointer' }} title="Lee el XML y separa los conceptos (comisión, fondo, etc.)">
          {importando ? 'Leyendo...' : <ConIcono nombre="codigo" size={13}>Importar XML (auto-llenar)</ConIcono>}
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 6, marginBottom: 4 }}>
        {['Motivo (cuenta contable)', 'Monto base', 'IVA', ''].map((h, i) => (
          <div key={i} style={{ fontSize: 12, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</div>
        ))}
      </div>
      {conceptos.map((c, i) => {
        const montoErr  = hasError && (!c.monto || isNaN(parseFloat(c.monto)) || parseFloat(c.monto) <= 0);
        const motivoErr = hasError && !c.motivo?.trim();
        return (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: COLS, gap: 6, marginBottom: 6, alignItems: 'start' }}>
            <MotivoSearch value={c.motivo} onChange={v => upd(i, 'motivo', v)} hasError={motivoErr} motivos={motivos} />
            <MontoInput value={c.monto} onChange={v => upd(i, 'monto', v)} err={montoErr} />
            <MontoInput value={c.iva} onChange={v => upd(i, 'iva', v)} />
            <button aria-label="Quitar concepto" title="Quitar concepto" onClick={() => remove(i)} disabled={conceptos.length <= 1} style={{
              borderRadius: 7, border: '1px solid var(--danger-line)', background: 'var(--danger-bg)',
              color: 'var(--danger)', cursor: conceptos.length <= 1 ? 'not-allowed' : 'pointer',
              fontSize: 14, opacity: conceptos.length <= 1 ? 0.3 : 1, fontFamily: 'Inter, sans-serif',
            }}><Icono nombre="papelera" size={15} /></button>
          </div>
        );
      })}
      <button onClick={add} style={{
        padding: '5px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600,
        border: '1.5px dashed var(--hair)', background: 'var(--hair-soft)', color: 'var(--glass)',
        cursor: 'pointer', marginTop: 2, fontFamily: 'Inter, sans-serif',
      }}>+ Agregar concepto</button>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 10, alignItems: 'baseline' }}>
        <span style={{ fontSize: 11.5, color: 'var(--glass)', textTransform: 'uppercase' }}>Base {fmt(base)} · IVA {fmt(iva)}</span>
        <span style={{ fontSize: 11.5, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Neto</span>
        <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--plasma)', fontFamily: 'var(--digit-font)' }}>{fmt(base + iva)}</span>
      </div>
      {hasError && <div style={{ color: 'var(--danger)', fontSize: 11.5, marginTop: 5 }}>Cada concepto necesita un motivo y un monto válido</div>}
    </div>
  );
}

// Editor de conceptos de Caja Chica: motivo, monto, XML propio y PDF propio por renglón
function ConceptosCajaChica({ conceptos, onChange, motivos, hasError, onImportXml, onAdjuntarPdf, importandoIdx }) {
  const upd = (i, field, val) => onChange(conceptos.map((c, idx) => idx === i ? { ...c, [field]: val } : c));
  const add = () => onChange([...conceptos, { motivo: '', monto: '', folioCfdi: '', serie: '', usoCfdi: '', metodoPago: '', fechaTimbrado: '' }]);
  const remove = (i) => { if (conceptos.length <= 1) return; onChange(conceptos.filter((_, idx) => idx !== i)); };
  const total = conceptos.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
  const fmt = n => `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
  return (
    <div>
      <div style={{ fontSize: 11.5, color: 'var(--glass)', marginBottom: 8 }}>
        Cada concepto puede tener su propio comprobante (XML + PDF). El total es la suma de todos los montos.
      </div>
      {conceptos.map((c, i) => {
        const montoErr  = hasError && (!c.monto || isNaN(parseFloat(c.monto)) || parseFloat(c.monto) <= 0);
        const motivoErr = hasError && !c.motivo?.trim();
        return (
          <div key={i} style={{ background: 'var(--hair-soft)', borderRadius: 10, border: '1px solid var(--hair)', padding: 12, marginBottom: 10 }}>
            {/* Fila 1: motivo + monto */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px 34px', gap: 8, marginBottom: 8 }}>
              <MotivoSearch value={c.motivo} onChange={v => upd(i, 'motivo', v)} hasError={motivoErr} motivos={motivos} />
              <MontoInput value={c.monto} onChange={v => upd(i, 'monto', v)} err={montoErr} />
              <button aria-label="Quitar concepto" title="Quitar concepto" onClick={() => remove(i)} disabled={conceptos.length <= 1} style={{ borderRadius: 7, border: '1px solid var(--danger-line)', background: 'var(--danger-bg)', color: 'var(--danger)', cursor: conceptos.length <= 1 ? 'not-allowed' : 'pointer', fontSize: 14, opacity: conceptos.length <= 1 ? 0.3 : 1 }}><Icono nombre="papelera" size={15} /></button>
            </div>
            {/* Fila 2: botones de archivo + datos del CFDI */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={() => onImportXml(i)} disabled={importandoIdx === i} style={{ ...css.btn('secundario'), padding: '4px 10px', fontSize: 11.5, cursor: importandoIdx === i ? 'wait' : 'pointer' }}>
                {importandoIdx === i ? 'Leyendo...' : <ConIcono nombre="codigo" size={13}>Importar XML</ConIcono>}
              </button>
              <button onClick={() => onAdjuntarPdf(i)} style={{ ...css.btn('secundario'), padding: '4px 10px', fontSize: 11.5 }}>
                <ConIcono nombre="clip" size={13}>Adjuntar PDF</ConIcono>
              </button>
              {c.folioCfdi && (
                <span style={{ fontSize: 11.5, fontFamily: 'var(--digit-font)', color: 'var(--ok)', background: 'var(--ok-bg)', padding: '2px 8px', borderRadius: 5 }}>
                  <Icono nombre="check" size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} />{c.folioCfdi.slice(0, 20)}… {c.metodoPago && `· ${c.metodoPago}`}
                </span>
              )}
              {c._impuestosImportes && Object.keys(c._impuestosImportes).length > 0 && (
                <span style={{ fontSize: 11.5, color: 'var(--st-por-pagar)', background: 'var(--info-bg)', padding: '2px 8px', borderRadius: 5 }}>
                  {Object.entries(c._impuestosImportes).map(([k, v]) => `${k}: $${(parseFloat(v)||0).toFixed(2)}`).join(' · ')}
                </span>
              )}
              {c._archivos?.length > 0 && (
                <span style={{ fontSize: 11.5, color: 'var(--glass)' }}><ConIcono nombre="carpeta" size={13}>{c._archivos.length} archivo(s)</ConIcono></span>
              )}
            </div>
          </div>
        );
      })}
      <button onClick={add} style={{ padding: '5px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600, border: '1.5px dashed var(--hair)', background: 'var(--hair-soft)', color: 'var(--glass)', cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
        + Agregar concepto
      </button>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10, alignItems: 'baseline' }}>
        <span style={{ fontSize: 11.5, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total</span>
        <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--plasma)', fontFamily: 'var(--digit-font)' }}>{fmt(total)}</span>
      </div>
      {hasError && <div style={{ color: 'var(--danger)', fontSize: 11.5, marginTop: 5 }}>Cada concepto necesita un motivo y un monto válido</div>}
    </div>
  );
}

export default function NuevaSolicitud({ onCreated, tipo = 'pago' }) {
  const cfg = TIPO_CFG[tipo] || TIPO_CFG.pago;
  // La restricción de motivos por proveedor solo aplica al pago normal
  const usaMotivosProveedor = tipo === 'pago';
  // Nómina: varios motivos con monto que suman o restan; sin CFDI/facturas ni monto único
  const esNomina = tipo === 'nomina';
  // Complemento a costo de importación: varios motivos con monto (todos suman),
  // catálogo de proveedores propio y los datos del embarque.
  const esImportacion = tipo === 'complemento_importacion';
  // Tipos cuyo adjunto acepta PDF y XML en el mismo control
  const adjuntosConXml = esImportacion;
  // Débito: el CFDI se parte en varios conceptos (motivo→cuenta + monto + IVA)
  const esDebito    = tipo === 'debito';
  // Caja chica: múltiples conceptos, cada uno con su propio XML + PDF
  const esCajaChica = tipo === 'caja_chica';
  // Reembolso de gastos: se comporta igual que caja chica (mismo editor de conceptos)
  const esReembolsoGastos = tipo === 'reembolso_gastos';
  // Contribuciones: varios impuestos en un mismo pago (IMSS, INFONAVIT, ISR
  // retenido...), cada uno con su motivo y su cuenta. Sin CFDI por renglón: se
  // amparan con la línea de captura y el acuse, no con factura.
  const esContribuciones = tipo === 'contribuciones';
  const { user } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [sucursalesAll, setSucursalesAll] = useState([]);
  const [centros, setCentros] = useState([]);
  const [motivosCatalogo, setMotivosCatalogo] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [errors, setErrors] = useState({});
  const [modoFacturas, setModoFacturas] = useState(false);
  // Conceptos de nómina (solo tipo 'nomina')
  const [conceptos, setConceptos] = useState([{ motivo: '', monto: '', operacion: 'suma' }]);
  // Conceptos de débito (solo tipo 'debito'): motivo→cuenta + monto base + IVA
  const [conceptosDeb, setConceptosDeb] = useState([{ motivo: '', monto: '', iva: '' }]);
  // Conceptos del complemento de importación (solo tipo 'complemento_importacion')
  const [conceptosImp, setConceptosImp] = useState([{ motivo: '', monto: '' }]);
  // Conceptos de contribuciones (solo tipo 'contribuciones'): motivo + monto
  const [conceptosContrib, setConceptosContrib] = useState([{ motivo: '', monto: '' }]);
  const [importandoImp, setImportandoImp] = useState(false);
  const impXmlRef = useRef(null);
  // XML por concepto del complemento (una factura por renglón)
  const [importandoImpIdx, setImportandoImpIdx] = useState(null);
  const impConceptoXmlRefs = useRef({}); // ref por índice de concepto
  const debitoXmlRef = useRef(null);
  const [importandoDeb, setImportandoDeb] = useState(false);
  // Conceptos de caja chica: motivo + monto + XML + PDF propios por renglón
  const [conceptosCC, setConceptosCC] = useState([{ motivo: '', monto: '', folioCfdi: '', serie: '', usoCfdi: '', metodoPago: '', fechaTimbrado: '', _archivos: [] }]);
  const [importandoCCIdx, setImportandoCCIdx] = useState(null);
  const ccXmlRefs = useRef({});   // ref por índice de concepto
  const ccPdfRefs = useRef({});
  // Conceptos de reembolso de gastos: idéntico a caja chica, propio estado/refs
  const [conceptosRG, setConceptosRG] = useState([{ motivo: '', monto: '', folioCfdi: '', serie: '', usoCfdi: '', metodoPago: '', fechaTimbrado: '', _archivos: [] }]);
  const [importandoRGIdx, setImportandoRGIdx] = useState(null);
  const rgXmlRefs = useRef({});
  const rgPdfRefs = useRef({});

  const [form, setForm] = useState({
    empresa: '', sucursal: '', centroCostos: '', direccion: '',
    proveedor: '', rfcProveedor: '', banco: '', clabe: '', cuenta: '', moneda: 'MXN',
    fechaSolicitud: hoyISO(),
    folioCfdi: '', notasCredito: '', monto: '',
    facturas: [],
    formaPago: 'TRANSFERENCIA ELECTRÓNICA', tipoPago: 'Programado', motivoPago: '', observaciones: '',
    impuestos: [], impuestosImportes: {},
    // Sólo complemento a costo de importación
    referenciaAduanal: '', numeroContenedor: '',
  });

  useEffect(() => {
    api.get('/empresas').then(all => {
      if (user?.rol === 'solicitante' && user?.empresas?.length) {
        const propias = all.filter(e => user.empresas.includes(e.nombre));
        setEmpresas(propias);
        if (propias.length === 1) setForm(f => ({ ...f, empresa: propias[0].nombre }));
      } else {
        setEmpresas(all);
      }
    }).catch(() => {});
    api.get('/sucursales').then(setSucursalesAll).catch(() => {});
    api.get(cfg.motivos).then(ms => setMotivosCatalogo(ms.map(m => m.nombre))).catch(() => {});
  }, [user, cfg.motivos]);

  // Cargar centros de costos de la colección CentroCostos filtrada por sucursal
  useEffect(() => {
    if (!form.sucursal) { setCentros([]); return; }
    api.get(`/centros?sucursal=${encodeURIComponent(form.sucursal)}`)
      .then(list => setCentros(
        user?.rol === 'solicitante' && user?.centrosCostos?.length
          ? list.filter(c => user.centrosCostos.includes(c.nombre))
          : list
      ))
      .catch(() => setCentros([]));
  }, [form.sucursal]);

  const empresaObj = empresas.find(e => e.nombre === form.empresa);

  // Sucursales filtradas por compañía; si la empresa no tiene asignadas, muestra todas
  const sucursalesBase = empresaObj?.sucursales?.length
    ? sucursalesAll.filter(s => empresaObj.sucursales.includes(s.nombre))
    : sucursalesAll;
  // Si el solicitante tiene sucursales asignadas, acota a ellas
  const sucursalesFiltradas = (user?.rol === 'solicitante' && user?.sucursales?.length)
    ? sucursalesBase.filter(s => user.sucursales.includes(s.nombre))
    : sucursalesBase;

  const totalNomina   = conceptos.reduce((s, c) => s + (parseFloat(c.monto) || 0) * (c.operacion === 'resta' ? -1 : 1), 0);
  // Neto caja chica = suma de bases + IVA acumulado de XMLs importados
  const totalCajaChicaBase = conceptosCC.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
  const totalCajaChicaIVA  = conceptosCC.reduce((s, c) => {
    return s + Object.entries(c._impuestosImportes || {}).reduce((a, [k, v]) => {
      return a + (k.startsWith('RET') ? -(parseFloat(v) || 0) : (parseFloat(v) || 0));
    }, 0);
  }, 0);
  const totalCajaChica = Math.round((totalCajaChicaBase + totalCajaChicaIVA) * 100) / 100;
  // Neto reembolso de gastos: idéntico cálculo que caja chica, sobre conceptosRG
  const totalRGBase = conceptosRG.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
  const totalRGIVA  = conceptosRG.reduce((s, c) => {
    return s + Object.entries(c._impuestosImportes || {}).reduce((a, [k, v]) => {
      return a + (k.startsWith('RET') ? -(parseFloat(v) || 0) : (parseFloat(v) || 0));
    }, 0);
  }, 0);
  const totalRG = Math.round((totalRGBase + totalRGIVA) * 100) / 100;
  const totalImportacion = conceptosImp.reduce((a, c) => a + (parseFloat(c.monto) || 0), 0);
  const totalContribuciones = conceptosContrib.reduce((a, c) => a + (parseFloat(c.monto) || 0), 0);
  // IVA leído del CFDI importado (importe exacto, no tasa × base)
  const totalBase = esCajaChica ? totalCajaChica : esReembolsoGastos ? totalRG : esNomina
    ? totalNomina
    : modoFacturas
      ? form.facturas.reduce((s, f) => s + (parseFloat(f.monto) || 0) * (f._egreso ? -1 : 1), 0)
      : parseFloat(form.monto) || 0;

  const toggleModo = () => {
    if (!modoFacturas) {
      setForm(f => ({ ...f, facturas: [{ folioCfdi: f.folioCfdi, notasCredito: f.notasCredito, monto: f.monto }] }));
    } else {
      const primera = form.facturas[0] || {};
      setForm(f => ({ ...f, folioCfdi: primera.folioCfdi || '', notasCredito: primera.notasCredito || '', monto: primera.monto || '', facturas: [] }));
    }
    setModoFacturas(m => !m);
    setErrors({});
  };

  // Al editar o quitar facturas se recalculan los impuestos exactos con los XML
  // que permanecen (si quitas un XML, sus impuestos dejan de sumar al total).
  const handleFacturasChange = (facturas) => {
    setForm(f => {
      const importes = {};
      const codigosXml = new Set();
      facturas.forEach(r => {
        Object.entries(r._impuestosImportes || {}).forEach(([k, v]) => {
          importes[k] = (importes[k] || 0) + v;
          codigosXml.add(k);
        });
      });
      // Impuestos marcados a mano (sin importe exacto de XML): se conservan
      const manuales = (f.impuestos || []).filter(c => !(c in (f.impuestosImportes || {})));
      return { ...f, facturas, impuestosImportes: importes, impuestos: [...new Set([...manuales, ...codigosXml])] };
    });
    if (errors.facturas) setErrors(e => ({ ...e, facturas: false }));
  };

  const xmlInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const [importando, setImportando] = useState(false);
  // Archivos que se subirán como comprobantes del expediente al crear la
  // solicitud: los XML importados + los PDF adjuntados por el usuario.
  const [adjuntos, setAdjuntos] = useState([]);

  const handleAdjuntarPDF = (fileList) => {
    // El complemento de importación admite también el XML del CFDI en el mismo
    // adjunto; en los demás tipos el XML entra por su propio "Importar XML", que
    // además lee los impuestos, así que aquí seguiría siendo sólo PDF.
    const extensiones = adjuntosConXml ? ['.pdf', '.xml'] : ['.pdf'];
    const files = Array.from(fileList || []).filter(f => extensiones.some(x => f.name.toLowerCase().endsWith(x)));
    // Este botón dice "Adjuntar PDF y XML", así que es donde la gente suelta el CFDI
    // esperando que se lea. Antes sólo lo archivaba —el que lo interpreta es el
    // "Importar XML" de los conceptos— y parecía que el sistema no leía el XML.
    // Ahora el XML del complemento se procesa aquí también: mismo resultado, caiga
    // donde caiga. handleImportXMLImportacion ya lo agrega al expediente por su cuenta.
    const xmls = esImportacion ? files.filter(f => f.name.toLowerCase().endsWith('.xml')) : [];
    const resto = files.filter(f => !xmls.includes(f));
    if (resto.length) setAdjuntos(a => [...a, ...resto]);
    if (xmls.length) handleImportXMLImportacion(xmls);
    if (pdfInputRef.current) pdfInputRef.current.value = '';
  };

  // Sube los adjuntos al expediente de la solicitud recién creada (quedan
  // visibles en Captura de Comprobantes y en el detalle del historial).
  const subirAdjuntos = async (solId) => {
    if (!adjuntos.length) return;
    try {
      for (let i = 0; i < adjuntos.length; i += 10) {
        const fd = new FormData();
        adjuntos.slice(i, i + 10).forEach(f => fd.append('archivos', f));
        fd.append('concepto', 'Factura');
        await api.upload(`/solicitudes/${solId}/comprobantes`, fd);
      }
      setAdjuntos([]);
    } catch (e) {
      toast_(`Solicitud creada, pero falló la subida de archivos: ${e.message}`, 'error');
    }
  };

  const handleImportXML = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f.name.toLowerCase().endsWith('.xml'));
    if (!files.length) return;
    setImportando(true);
    try {
      const nuevas = [];
      const archivosOk = []; // XML leídos con éxito: se adjuntan al crear
      const impuestosDetectados = new Set(form.impuestos);
      const importesAcum = { ...(form.impuestosImportes || {}) };
      const errores = [];

      // Folios ya presentes en la lista actual (para no duplicar)
      const existentes = new Set(
        (modoFacturas ? form.facturas : [{ folioCfdi: form.folioCfdi }])
          .map(f => (f.folioCfdi || '').trim()).filter(Boolean)
      );

      for (const file of files) {
        try {
          const cfdi = parseCFDI(await leerArchivo(file), file.name);
          const folio = cfdi.folioFiscal || cfdi.referencia;
          if (existentes.has(folio)) { errores.push(`${file.name}: repetida`); continue; }
          existentes.add(folio);
          // Bloqueo: el CFDI debe ser del proveedor seleccionado (cuando conocemos su RFC)
          if (cfdi.rfcEmisor && form.rfcProveedor && cfdi.rfcEmisor.toUpperCase() !== form.rfcProveedor.toUpperCase()) {
            errores.push(`${file.name}: el CFDI es de ${cfdi.rfcEmisor} y NO corresponde al proveedor seleccionado (${form.rfcProveedor})`);
            continue;
          }
          cfdi.impuestos.forEach(c => impuestosDetectados.add(c));
          // Nota de crédito (Egreso): sus impuestos restan del neto.
          const sgn = cfdi.esEgreso ? -1 : 1;
          Object.entries(cfdi.impuestosImportes || {}).forEach(([k, v]) => { importesAcum[k] = (importesAcum[k] || 0) + v * sgn; });
          nuevas.push({
            // Nota de crédito: su folio va en "Notas de Crédito", no en "Folio CFDI".
            folioCfdi: cfdi.esEgreso ? '' : folio,
            notasCredito: cfdi.esEgreso ? folio : '',
            monto: cfdi.base ? String(cfdi.base.toFixed(2)) : (cfdi.total ? String(cfdi.total.toFixed(2)) : ''),
            serie: cfdi.serie || '', usoCfdi: cfdi.usoCfdi || '', metodoPago: cfdi.metodoPago || '', fechaTimbrado: cfdi.fechaTimbrado || '',
            ...datosFiscales(cfdi),
            _xml: true, _rfc: cfdi.rfcEmisor, _emisor: cfdi.nombreEmisor, _total: cfdi.total, _egreso: cfdi.esEgreso,
            _sinTimbrar: cfdi.sinTimbrar,
            // Impuestos propios de este CFDI (con signo): permiten restarlos si se quita la factura
            _impuestosImportes: Object.fromEntries(Object.entries(cfdi.impuestosImportes || {}).map(([k, v]) => [k, v * sgn])),
          });
          archivosOk.push(file);
        } catch (e) { errores.push(e.message); }
      }

      if (!nuevas.length) {
        toast_(errores[0] || 'No se pudo leer ningún XML', 'error');
        return;
      }
      setAdjuntos(a => [...a, ...archivosOk]); // el XML también se guarda en el expediente

      // Validar duplicados contra solicitudes ya guardadas
      await Promise.all(nuevas.map(async (f) => {
        try {
          const r = await api.get(`/solicitudes/check-cfdi?folio=${encodeURIComponent(f.folioCfdi)}`);
          if (r.duplicado && r.en) f._dup = r.en.folio;
        } catch {}
      }));
      // Duplicados dentro del propio lote (por si dos archivos traen el mismo UUID)
      const vistos = {};
      nuevas.forEach(f => { const k = f.folioCfdi || f.notasCredito; if (vistos[k]) f._dup = 'local'; vistos[k] = true; });

      // Fusionar con las facturas existentes (descartando renglones vacíos)
      const previas = modoFacturas
        ? form.facturas.filter(f => (f.folioCfdi || '').trim() || (parseFloat(f.monto) || 0) > 0)
        : [];
      const facturas = [...previas, ...nuevas];

      // Con un solo CFDI, sus datos fiscales se guardan también a nivel solicitud:
      // así sobreviven aunque las filas se rearmen (alternar el modo de captura)
      // o se mezclen con filas capturadas a mano.
      const unico = nuevas.length === 1 && previas.length === 0 ? nuevas[0] : null;
      setForm(f => ({
        ...f, facturas, impuestos: [...impuestosDetectados], impuestosImportes: importesAcum,
        ...(unico ? soloFiscales(unico) : {}),
      }));
      setModoFacturas(true);
      setErrors({});

      let msg = `${nuevas.length} factura${nuevas.length !== 1 ? 's' : ''} importada${nuevas.length !== 1 ? 's' : ''}`;
      if (impuestosDetectados.size > form.impuestos.length) msg += ' · impuestos detectados';
      if (errores.length) msg += ` · ${errores.length} omitida${errores.length !== 1 ? 's' : ''}`;
      const sinTimbre = nuevas.filter(f => f._sinTimbrar).length;
      if (sinTimbre) msg += ` · ${sinTimbre} SIN TIMBRAR (pide el CFDI timbrado)`;
      toast_(msg, (errores.length || sinTimbre) ? 'error' : 'ok');
    } finally {
      setImportando(false);
      if (xmlInputRef.current) xmlInputRef.current.value = '';
    }
  };

  // Débito: auto-llenar los conceptos desde el XML (cada concepto del CFDI → un renglón
  // con su importe base e IVA; el usuario asigna el motivo/cuenta de cada uno).
  const handleImportXMLDebito = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f.name.toLowerCase().endsWith('.xml'));
    if (!files.length) return;
    setImportandoDeb(true);
    try {
      const cfdi = parseCFDI(await leerArchivo(files[0]), files[0].name);
      if (cfdi.rfcEmisor && form.rfcProveedor && cfdi.rfcEmisor.toUpperCase() !== form.rfcProveedor.toUpperCase()) {
        toast_(`El CFDI es de ${cfdi.rfcEmisor} y NO corresponde al proveedor seleccionado (${form.rfcProveedor})`, 'error');
        return;
      }
      const rows = (cfdi.conceptos || []).map(c => ({ motivo: '', monto: c.importe ? String(c.importe) : '', iva: c.iva ? String(c.iva) : '' }));
      if (!rows.length) { toast_('El XML no trae conceptos', 'error'); return; }
      setConceptosDeb(rows);
      // El débito parte UN CFDI en varios conceptos, así que los datos fiscales
      // son de la solicitud completa: se guardan planos (no hay arreglo de
      // facturas en este tipo).
      // Sin timbrar no hay folio fiscal: se identifica por serie-folio (ver `referencia`)
      setForm(f => ({ ...f, folioCfdi: cfdi.folioFiscal || cfdi.referencia || f.folioCfdi, ...datosFiscales(cfdi) }));
      if (cfdi.sinTimbrar) toast_(`El XML de ${cfdi.referencia} NO está timbrado: no ampara el gasto ni permite acreditar el IVA. Pide a ${cfdi.nombreEmisor || 'el proveedor'} el CFDI timbrado.`, 'error');
      setAdjuntos(a => [...a, files[0]]); // el XML se guarda en el expediente
      toast_(`${rows.length} concepto${rows.length !== 1 ? 's' : ''} importado${rows.length !== 1 ? 's' : ''} — asigna el motivo/cuenta de cada uno`);
    } catch (e) { toast_(e.message, 'error'); }
    finally { setImportandoDeb(false); if (debitoXmlRef.current) debitoXmlRef.current.value = ''; }
  };

  // Complemento a costo de importación: la factura del agente aduanal trae varios
  // conceptos (pedimento, honorarios, servicios complementarios), todos con su IVA.
  // Se prellena un renglón por concepto con su importe y se guarda el IVA REAL del
  // CFDI, no una tasa calculada: el neto tiene que cuadrar al peso con la factura.
  // El motivo lo elige el usuario de su catálogo — la descripción del CFDI se
  // queda como pista al lado, porque no coincide con los nombres del catálogo.
  const handleImportXMLImportacion = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f.name.toLowerCase().endsWith('.xml'));
    if (!files.length) return;
    // Este import REEMPLAZA los renglones: si alguno ya tiene su propia factura
    // adjunta, hay que avisar antes de tirarla.
    const conCfdi = conceptosImp.filter(c => c.folioCfdi).length;
    if (conCfdi && !window.confirm(
      `Ya hay ${conCfdi} concepto(s) con su propio CFDI adjunto.\n\n` +
      'Auto-llenar reemplaza todos los conceptos con los de este XML y descarta esas facturas. ¿Continuar?'
    )) { if (impXmlRef.current) impXmlRef.current.value = ''; return; }
    setImportandoImp(true);
    try {
      const cfdi = parseCFDI(await leerArchivo(files[0]), files[0].name);
      if (cfdi.rfcEmisor && form.rfcProveedor && cfdi.rfcEmisor.toUpperCase() !== form.rfcProveedor.toUpperCase()) {
        toast_(`El CFDI es de ${cfdi.rfcEmisor} y NO corresponde al proveedor seleccionado (${form.rfcProveedor})`, 'error');
        return;
      }
      const filas = (cfdi.conceptos || []).map(c => ({
        motivo: '', monto: c.importe ? String(c.importe) : '', _descripcion: c.descripcion || '',
      })).filter(f => f.monto);
      if (!filas.length) { toast_('El XML no trae conceptos con importe', 'error'); return; }
      setConceptosImp(filas);
      setForm(f => ({
        ...f,
        // Sin timbrar no hay folio fiscal: se identifica por serie-folio
        folioCfdi: cfdi.folioFiscal || cfdi.referencia || f.folioCfdi,
        moneda: cfdi.moneda || f.moneda,
        ...datosFiscales(cfdi),
        impuestos: cfdi.impuestos || [],
        impuestosImportes: cfdi.impuestosImportes || {},
      }));
      setAdjuntos(a => [...a, files[0]]); // el XML se guarda en el expediente
      if (cfdi.sinTimbrar) toast_(`El XML de ${cfdi.referencia} NO está timbrado: no ampara el gasto ni permite acreditar el IVA.`, 'error');
      else toast_(`${filas.length} concepto${filas.length !== 1 ? 's' : ''} importado${filas.length !== 1 ? 's' : ''} — asigna el motivo de cada uno`);
    } catch (e) { toast_(e.message, 'error'); }
    finally { setImportandoImp(false); if (impXmlRef.current) impXmlRef.current.value = ''; }
  };

  // Complemento de importación: XML por concepto. El costo de una importación
  // llega en varias facturas (agente aduanal, naviera, transportista, honorarios),
  // así que cada renglón puede traer la suya con sus impuestos reales — que es lo
  // que el reporte necesita para desglosar sin repartir a prorrata.
  const handleImportXMLImpConcepto = async (idx, fileList) => {
    const files = Array.from(fileList || []).filter(f => f.name.toLowerCase().endsWith('.xml'));
    if (!files.length) return;
    setImportandoImpIdx(idx);
    try {
      const cfdi = parseCFDI(await leerArchivo(files[0]), files[0].name);
      const repetido = cfdiYaUsado(conceptosImp, idx, cfdi);
      if (repetido) { toast_(repetido, 'error'); return; }
      setConceptosImp(prev => prev.map((c, i) => i !== idx ? c : {
        ...c,
        folioCfdi: cfdi.folioFiscal || cfdi.referencia || '',
        serie: cfdi.serie || '',
        usoCfdi: cfdi.usoCfdi || '',
        metodoPago: cfdi.metodoPago || '',
        fechaTimbrado: cfdi.fechaTimbrado || '',
        ...datosFiscales(cfdi),
        // El monto capturado manda: la factura puede amparar más de lo que se
        // carga a esta importación (un flete consolidado, por ejemplo).
        monto: c.monto || (cfdi.base ? String(cfdi.base.toFixed(2)) : ''),
        _descripcion: c._descripcion || cfdi.concepto || '',
        _impuestos: cfdi.impuestos || [],
        _impuestosImportes: cfdi.impuestosImportes || {},
        _archivos: [...(c._archivos || []), files[0]],
      }));
      setAdjuntos(a => [...a, files[0]]);
      if (cfdi.sinTimbrar) toast_(`El XML de ${cfdi.referencia} NO está timbrado: no ampara el gasto ni permite acreditar el IVA.`, 'error');
      else toast_(`Concepto ${idx + 1}: XML de ${cfdi.nombreEmisor || cfdi.rfcEmisor} importado`);
    } catch (e) { toast_(e.message, 'error'); }
    finally { setImportandoImpIdx(null); if (impConceptoXmlRefs.current[idx]) impConceptoXmlRefs.current[idx].value = ''; }
  };

  // Caja chica: importar XML para un concepto específico
  const handleImportXMLCC = async (idx, fileList) => {
    const files = Array.from(fileList || []).filter(f => f.name.toLowerCase().endsWith('.xml'));
    if (!files.length) return;
    setImportandoCCIdx(idx);
    try {
      const cfdi = parseCFDI(await leerArchivo(files[0]), files[0].name);
      const repetido = cfdiYaUsado(conceptosCC, idx, cfdi);
      if (repetido) { toast_(repetido, 'error'); return; }
      setConceptosCC(prev => prev.map((c, i) => i !== idx ? c : {
        ...c,
        folioCfdi: cfdi.folioFiscal || cfdi.referencia || '',
        serie: cfdi.serie || '',
        usoCfdi: cfdi.usoCfdi || '',
        metodoPago: cfdi.metodoPago || '',
        fechaTimbrado: cfdi.fechaTimbrado || '',
        ...datosFiscales(cfdi),
        monto: c.monto || (cfdi.base ? String(cfdi.base.toFixed(2)) : ''),
        // Impuestos exactos del XML para este concepto
        _impuestos: cfdi.impuestos || [],
        _impuestosImportes: cfdi.impuestosImportes || {},
        _archivos: [...(c._archivos || []), files[0]],
      }));
      setAdjuntos(a => [...a, files[0]]);
      toast_(`Concepto ${idx + 1}: XML importado`);
    } catch (e) { toast_(e.message, 'error'); }
    finally { setImportandoCCIdx(null); if (ccXmlRefs.current[idx]) ccXmlRefs.current[idx].value = ''; }
  };

  // Caja chica: adjuntar PDF a un concepto
  const handleAdjuntarPDFCC = (idx, fileList) => {
    const files = Array.from(fileList || []).filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (!files.length) return;
    setConceptosCC(prev => prev.map((c, i) => i !== idx ? c : { ...c, _archivos: [...(c._archivos || []), ...files] }));
    setAdjuntos(a => [...a, ...files]);
    if (ccPdfRefs.current[idx]) ccPdfRefs.current[idx].value = '';
  };

  // Reembolso de gastos: importar XML para un concepto específico (idéntico a caja chica)
  const handleImportXMLRG = async (idx, fileList) => {
    const files = Array.from(fileList || []).filter(f => f.name.toLowerCase().endsWith('.xml'));
    if (!files.length) return;
    setImportandoRGIdx(idx);
    try {
      const cfdi = parseCFDI(await leerArchivo(files[0]), files[0].name);
      const repetido = cfdiYaUsado(conceptosRG, idx, cfdi);
      if (repetido) { toast_(repetido, 'error'); return; }
      setConceptosRG(prev => prev.map((c, i) => i !== idx ? c : {
        ...c,
        folioCfdi: cfdi.folioFiscal || cfdi.referencia || '',
        serie: cfdi.serie || '',
        usoCfdi: cfdi.usoCfdi || '',
        metodoPago: cfdi.metodoPago || '',
        fechaTimbrado: cfdi.fechaTimbrado || '',
        ...datosFiscales(cfdi),
        monto: c.monto || (cfdi.base ? String(cfdi.base.toFixed(2)) : ''),
        _impuestos: cfdi.impuestos || [],
        _impuestosImportes: cfdi.impuestosImportes || {},
        _archivos: [...(c._archivos || []), files[0]],
      }));
      setAdjuntos(a => [...a, files[0]]);
      toast_(`Concepto ${idx + 1}: XML importado`);
    } catch (e) { toast_(e.message, 'error'); }
    finally { setImportandoRGIdx(null); if (rgXmlRefs.current[idx]) rgXmlRefs.current[idx].value = ''; }
  };

  // Reembolso de gastos: adjuntar PDF a un concepto
  const handleAdjuntarPDFRG = (idx, fileList) => {
    const files = Array.from(fileList || []).filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (!files.length) return;
    setConceptosRG(prev => prev.map((c, i) => i !== idx ? c : { ...c, _archivos: [...(c._archivos || []), ...files] }));
    setAdjuntos(a => [...a, ...files]);
    if (rgPdfRefs.current[idx]) rgPdfRefs.current[idx].value = '';
  };

  // CFDI de otro RFC: bloquea el envío aunque el proveedor se elija DESPUÉS de importar
  const cfdiAjeno = () => {
    if (!form.rfcProveedor) return null;
    return (modoFacturas ? form.facturas : [])
      .find(f => f._xml && f._rfc && f._rfc.toUpperCase() !== form.rfcProveedor.toUpperCase()) || null;
  };

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (errors[k]) setErrors(e => ({ ...e, [k]: false })); };

  const handleEmpresa = (nombre) => {
    setForm(f => ({ ...f, empresa: nombre, sucursal: '', centroCostos: '', direccion: '' }));
    if (errors.empresa) setErrors(e => ({ ...e, empresa: false }));
  };
  const handleSucursal = (s) => { set('sucursal', s); set('centroCostos', ''); };
  const handleCentro = (nombre) => {
    const centroObj = centros.find(cc => cc.nombre === nombre);
    const dir = centroObj?.seccion?.trim() || centroObj?.direccion?.trim() || '';
    setForm(f => ({ ...f, centroCostos: nombre, ...(dir ? { direccion: dir } : {}) }));
    if (errors.centroCostos) setErrors(e => ({ ...e, centroCostos: false }));
  };
  const [plantillas, setPlantillas] = useState([]);
  const [plantillaModal, setPlantillaModal] = useState(false);
  const [guardarPlantillaModal, setGuardarPlantillaModal] = useState(false);
  const [nombrePlantilla, setNombrePlantilla] = useState('');

  const cargarPlantillas = useCallback(async () => {
    try { setPlantillas(await api.get('/plantillas')); } catch {}
  }, []);

  useEffect(() => { cargarPlantillas(); }, [cargarPlantillas]);

  const aplicarPlantilla = (p) => {
    setForm(f => ({
      ...f,
      empresa: p.empresa || f.empresa,
      sucursal: p.sucursal || '',
      centroCostos: p.centroCostos || '',
      direccion: p.direccion || '',
      proveedor: p.proveedor || '',
      banco: p.banco || '',
      clabe: p.clabe || '',
      cuenta: p.cuenta || '',
      moneda: p.moneda || 'MXN',
      formaPago: p.formaPago || 'TRANSFERENCIA ELECTRÓNICA',
      tipoPago: p.tipoPago || 'Programado',
      motivoPago: p.motivoPago || '',    }));
    setPlantillaModal(false);
    toast_(`Plantilla "${p.nombre}" aplicada`);
    // Las cuentas del proveedor pudieron cambiar desde que se guardó la plantilla
    if (p.proveedor && !cfg.proveedores) {
      api.get(`/proveedores?q=${encodeURIComponent(p.proveedor)}`).then(r => {
        const prov = (r.items || []).find(x => x.nombre === p.proveedor);
        if (!prov) return;
        const c = prepararCuentas(prov, String(p.clabe || p.cuenta || '').replace(/\D/g, ''));
        setForm(f => (f.proveedor === p.proveedor ? { ...f, banco: c?.banco || '', clabe: c?.clabe || '', cuenta: c?.cuenta || '' } : f));
      }).catch(() => {});
    }
  };

  const guardarComoPlantilla = async () => {
    if (!nombrePlantilla.trim()) return;
    try {
      await api.post('/plantillas', {
        nombre: nombrePlantilla.trim(),
        empresa: form.empresa, sucursal: form.sucursal, centroCostos: form.centroCostos,
        direccion: form.direccion, proveedor: form.proveedor, banco: form.banco,
        clabe: form.clabe, cuenta: form.cuenta, moneda: form.moneda,
        formaPago: form.formaPago, tipoPago: form.tipoPago,
        motivoPago: form.motivoPago,
      });
      toast_(`Plantilla "${nombrePlantilla.trim()}" guardada`);
      setNombrePlantilla('');
      setGuardarPlantillaModal(false);
      cargarPlantillas();
    } catch (e) { toast_(e.message, 'error'); }
  };

  const eliminarPlantilla = async (id) => {
    if (!window.confirm('¿Eliminar esta plantilla?')) return;
    try { await api.del(`/plantillas/${id}`); cargarPlantillas(); } catch {}
  };

  const [cfdiDuplicado, setCfdiDuplicado] = useState(null);
  const cfdiTimer = useRef(null);

  const checkCfdi = useCallback(async (folio) => {
    if (!folio || folio.length < 3) { setCfdiDuplicado(null); return; }
    try {
      const r = await api.get(`/solicitudes/check-cfdi?folio=${encodeURIComponent(folio)}`);
      setCfdiDuplicado(r.duplicado ? r.en : null);
    } catch { setCfdiDuplicado(null); }
  }, []);

  const [proveedorMotivos, setProveedorMotivos] = useState([]);
  // La restricción de motivos por proveedor (motivosPago) aplica solo a pago;
  // en los otros tipos siempre se ofrece el catálogo completo de su motivo.
  const motivosVisibles = (usaMotivosProveedor && proveedorMotivos.length) ? proveedorMotivos : motivosCatalogo;

  // Proveedor con varias cuentas: el solicitante elige a cuál se paga o pide que
  // la confirme Cuentas por Pagar (components/CuentasProveedor.js)
  const [cuentasProv, setCuentasProv] = useState({ proveedor: '', cuentas: [] });
  const [eleccionCuenta, setEleccionCuenta] = useState('');
  const variasCuentas = cuentasProv.proveedor === form.proveedor && cuentasProv.cuentas.length > 1;
  const elegirCuenta = (clave, cuentas = cuentasProv.cuentas) => {
    setEleccionCuenta(clave);
    const c = cuentas.find(x => claveCuenta(x) === clave);
    setForm(f => ({ ...f, banco: c?.banco || '', clabe: c?.clabe || '', cuenta: c?.cuenta || '' }));
    if (errors.cuentaProveedor) setErrors(e => ({ ...e, cuentaProveedor: false }));
  };
  // Prepara la elección para un proveedor del catálogo; `clave` = cuenta ya conocida (plantilla)
  const prepararCuentas = (p, clave = '') => {
    const cuentas = cuentasDelProveedor(p);
    setCuentasProv({ proveedor: p?.nombre || '', cuentas });
    const conocida = clave && cuentas.find(c => claveCuenta(c) === clave);
    const unica = cuentas.length === 1 ? cuentas[0] : null;
    const c = conocida || unica;
    setEleccionCuenta(c ? claveCuenta(c) : '');
    return c;
  };

  const handleProveedor = (p) => {
    const c = prepararCuentas(p);
    setForm(f => ({
      ...f,
      proveedor: p ? p.nombre : '',
      rfcProveedor: p?.rfc || '',
      banco: c?.banco || '',
      clabe: c?.clabe || '',
      cuenta: c?.cuenta || '',
      moneda: p?.moneda || 'MXN',
      motivoPago: '',
    }));
    setProveedorMotivos(p?.motivosPago?.length ? p.motivosPago : []);
    if (p && errors.proveedor) setErrors(e => ({ ...e, proveedor: false }));
  };

  const validate = () => {
    const e = {};
    if (!form.empresa) e.empresa = true;
    if (!form.sucursal) e.sucursal = true;
    if (!form.centroCostos) e.centroCostos = true;
    if (!form.proveedor) e.proveedor = true;
    if (variasCuentas && !eleccionCuenta) e.cuentaProveedor = true;
    if (form.observaciones.trim().length < 50) e.observaciones = true; // concepto detallado: mínimo 50 caracteres
    if (esCajaChica) {
      const invalidos = conceptosCC.length === 0 ||
        conceptosCC.some(c => !c.motivo?.trim() || !c.monto || isNaN(parseFloat(c.monto)) || parseFloat(c.monto) <= 0);
      if (invalidos) e.conceptosCC = true;
    } else if (esReembolsoGastos) {
      const invalidos = conceptosRG.length === 0 ||
        conceptosRG.some(c => !c.motivo?.trim() || !c.monto || isNaN(parseFloat(c.monto)) || parseFloat(c.monto) <= 0);
      if (invalidos) e.conceptosRG = true;
    } else if (esDebito) {
      const invalidos = conceptosDeb.length === 0 ||
        conceptosDeb.some(c => !c.motivo?.trim() || !c.monto || isNaN(parseFloat(c.monto)) || parseFloat(c.monto) <= 0);
      if (invalidos) e.conceptosDeb = true;
    } else if (esNomina) {
      const invalidos = conceptos.length === 0 ||
        conceptos.some(c => !c.motivo?.trim() || !c.monto || isNaN(parseFloat(c.monto)) || parseFloat(c.monto) <= 0);
      if (invalidos) e.conceptos = true;
    } else if (esImportacion) {
      const invalidos = conceptosImp.length === 0 ||
        conceptosImp.some(c => !c.motivo?.trim() || !c.monto || isNaN(parseFloat(c.monto)) || parseFloat(c.monto) <= 0);
      if (invalidos) e.conceptosImp = true;
      // Sin referencia aduanal el complemento no se puede amarrar a su embarque
      if (!form.referenciaAduanal.trim()) e.referenciaAduanal = true;
    } else if (esContribuciones) {
      const invalidos = conceptosContrib.length === 0 ||
        conceptosContrib.some(c => !c.motivo?.trim() || !c.monto || isNaN(parseFloat(c.monto)) || parseFloat(c.monto) <= 0);
      if (invalidos) e.conceptosContrib = true;
    } else {
      if (!form.motivoPago.trim()) e.motivoPago = true;
      if (modoFacturas) {
        const invalidas = form.facturas.length === 0 ||
          form.facturas.some(f => !f.monto || isNaN(parseFloat(f.monto)) || parseFloat(f.monto) <= 0);
        if (invalidas) e.facturas = true;
      } else {
        if (!form.monto || isNaN(parseFloat(form.monto))) e.monto = true;
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 4000); };

  const buildPayload = () => {
    if (esCajaChica) {
      // Los impuestos de cada concepto se mandan además de acumulados abajo: el
      // reporte desglosa un renglón por concepto y necesita el IVA real de SU
      // factura, no la suma de todas repartida a prorrata.
      const items = conceptosCC.map(c => ({ motivo: (c.motivo || '').trim(), monto: parseFloat(c.monto) || 0, folioCfdi: c.folioCfdi || '', serie: c.serie || '', usoCfdi: c.usoCfdi || '', metodoPago: c.metodoPago || '', fechaTimbrado: c.fechaTimbrado || '', ...soloFiscales(c), impuestos: c._impuestos || [], impuestosImportes: c._impuestosImportes || {} }));
      const total = items.reduce((s, c) => s + c.monto, 0);
      const motivoResumen = items.length === 1 ? items[0].motivo : `Caja Chica (${items.length} conceptos)`;
      // Acumular impuestos de todos los conceptos XML
      const impCodigosSet = new Set();
      const impImportesAcum = {};
      conceptosCC.forEach(c => {
        (c._impuestos || []).forEach(k => impCodigosSet.add(k));
        Object.entries(c._impuestosImportes || {}).forEach(([k, v]) => {
          impImportesAcum[k] = Math.round(((impImportesAcum[k] || 0) + (parseFloat(v) || 0)) * 100) / 100;
        });
      });
      return { ...form, tipo, monto: total, motivoPago: motivoResumen, conceptosCajaChica: items, facturas: [], impuestos: [...impCodigosSet], impuestosImportes: impImportesAcum };
    }
    if (esReembolsoGastos) {
      const items = conceptosRG.map(c => ({ motivo: (c.motivo || '').trim(), monto: parseFloat(c.monto) || 0, folioCfdi: c.folioCfdi || '', serie: c.serie || '', usoCfdi: c.usoCfdi || '', metodoPago: c.metodoPago || '', fechaTimbrado: c.fechaTimbrado || '', ...soloFiscales(c), impuestos: c._impuestos || [], impuestosImportes: c._impuestosImportes || {} }));
      const total = items.reduce((s, c) => s + c.monto, 0);
      const motivoResumen = items.length === 1 ? items[0].motivo : `Reembolso de Gastos (${items.length} conceptos)`;
      const impCodigosSet = new Set();
      const impImportesAcum = {};
      conceptosRG.forEach(c => {
        (c._impuestos || []).forEach(k => impCodigosSet.add(k));
        Object.entries(c._impuestosImportes || {}).forEach(([k, v]) => {
          impImportesAcum[k] = Math.round(((impImportesAcum[k] || 0) + (parseFloat(v) || 0)) * 100) / 100;
        });
      });
      return { ...form, tipo, monto: total, motivoPago: motivoResumen, conceptosReembolsoGastos: items, facturas: [], impuestos: [...impCodigosSet], impuestosImportes: impImportesAcum };
    }
    if (esDebito) {
      const items = conceptosDeb.map(c => ({ motivo: (c.motivo || '').trim(), monto: parseFloat(c.monto) || 0, iva: parseFloat(c.iva) || 0 }));
      const totalBaseDeb = items.reduce((a, c) => a + c.monto, 0);
      const motivoResumen = items.length === 1 ? items[0].motivo : `Débito (${items.length} conceptos)`;
      // El backend recalcula monto/IVA/cuenta por concepto; se envía todo para que quede consistente
      return { ...form, tipo, monto: totalBaseDeb, motivoPago: motivoResumen, conceptosDebito: items, facturas: [] };
    }
    if (esContribuciones) {
      // Cada impuesto con su motivo y su monto; el backend recalcula el total y la
      // cuenta contable de cada uno. motivoPago resume para historial y reporte.
      const items = conceptosContrib.map(c => ({ motivo: (c.motivo || '').trim(), monto: parseFloat(c.monto) || 0 }));
      const motivoResumen = items.length === 1 ? items[0].motivo : `Contribuciones (${items.length} conceptos)`;
      return { ...form, tipo, monto: totalContribuciones, motivoPago: motivoResumen, conceptosContribuciones: items, facturas: [] };
    }
    if (esImportacion) {
      // Cada concepto puede traer SU factura (agente aduanal, naviera, honorarios…),
      // así que viajan sus datos fiscales y sus impuestos reales, como en caja chica.
      const items = conceptosImp.map(c => ({
        motivo: (c.motivo || '').trim(), monto: parseFloat(c.monto) || 0,
        folioCfdi: c.folioCfdi || '', serie: c.serie || '', usoCfdi: c.usoCfdi || '',
        metodoPago: c.metodoPago || '', fechaTimbrado: c.fechaTimbrado || '',
        ...soloFiscales(c), impuestos: c._impuestos || [], impuestosImportes: c._impuestosImportes || {},
      }));
      // El backend recalcula el total y la cuenta de cada concepto; motivoPago
      // (requerido por el modelo) resume los conceptos para historial y reporte.
      const motivoResumen = items.length === 1 ? items[0].motivo : `Complemento a Costo de Importación (${items.length} conceptos)`;
      // Impuestos a nivel solicitud = suma de los de cada factura. Si ningún renglón
      // trae la suya, se conservan los del CFDI único importado con "auto-llenar"
      // (no se mezclan: contaría dos veces la misma factura).
      const hayPorConcepto = items.some(c => Object.keys(c.impuestosImportes || {}).length);
      const impCodigos = new Set();
      const impImportes = {};
      if (hayPorConcepto) {
        items.forEach(c => {
          (c.impuestos || []).forEach(k => impCodigos.add(k));
          Object.entries(c.impuestosImportes || {}).forEach(([k, v]) => {
            impImportes[k] = Math.round(((impImportes[k] || 0) + (parseFloat(v) || 0)) * 100) / 100;
          });
        });
      }
      return {
        ...form, tipo, monto: totalImportacion, motivoPago: motivoResumen,
        conceptosImportacion: items, facturas: [],
        ...(hayPorConcepto ? { impuestos: [...impCodigos], impuestosImportes: impImportes } : {}),
      };
    }
    if (esNomina) {
      const items = conceptos.map(c => ({ motivo: c.motivo.trim(), monto: parseFloat(c.monto) || 0, operacion: c.operacion }));
      // motivoPago (requerido por el modelo) resume los conceptos para el historial/reporte
      const motivoResumen = items.length === 1 ? items[0].motivo : `Nómina (${items.length} conceptos)`;
      return { ...form, tipo, monto: totalNomina, motivoPago: motivoResumen, conceptosNomina: items, facturas: [], impuestos: [], impuestosImportes: {} };
    }
    return modoFacturas
      ? { ...form, tipo, monto: totalBase, facturas: form.facturas.map(f => ({ folioCfdi: f.folioCfdi, notasCredito: f.notasCredito, monto: (parseFloat(f.monto) || 0) * (f._egreso ? -1 : 1), serie: f.serie || '', usoCfdi: f.usoCfdi || '', metodoPago: f.metodoPago || '', fechaTimbrado: f.fechaTimbrado || '', ...soloFiscales(f) })) }
      : { ...form, tipo, monto: parseFloat(form.monto), facturas: [] };
  };

  const resetCampos = () => {
    if (esCajaChica) {
      setConceptosCC([{ motivo: '', monto: '', folioCfdi: '', serie: '', usoCfdi: '', metodoPago: '', fechaTimbrado: '', _archivos: [] }]);
      setForm(f => ({ ...f, observaciones: '' }));
      return;
    }
    if (esReembolsoGastos) {
      setConceptosRG([{ motivo: '', monto: '', folioCfdi: '', serie: '', usoCfdi: '', metodoPago: '', fechaTimbrado: '', _archivos: [] }]);
      setForm(f => ({ ...f, observaciones: '' }));
      return;
    }
    if (esDebito) {
      setConceptosDeb([{ motivo: '', monto: '', iva: '' }]);
      setForm(f => ({ ...f, folioCfdi: '', observaciones: '' }));
      return;
    }
    if (esNomina) {
      setConceptos([{ motivo: '', monto: '', operacion: 'suma' }]);
      setForm(f => ({ ...f, observaciones: '' }));
      return;
    }
    if (esImportacion) {
      setConceptosImp([{ motivo: '', monto: '' }]);
      setForm(f => ({ ...f, observaciones: '', referenciaAduanal: '', numeroContenedor: '', folioCfdi: '', impuestos: [], impuestosImportes: {} }));
      return;
    }
    return modoFacturas
      ? setForm(f => ({ ...f, facturas: [{ folioCfdi: '', notasCredito: '', monto: '' }], motivoPago: '', observaciones: '' }))
      : setForm(f => ({ ...f, folioCfdi: '', notasCredito: '', monto: '', motivoPago: '', observaciones: '' }));
  };

  const handleGuardar = async () => {
    if (!validate()) { toast_('Completa los campos requeridos', 'error'); return; }
    const ajeno = cfdiAjeno();
    if (ajeno) {
      toast_(`El CFDI ${ajeno.folioCfdi || ajeno.notasCredito} es de ${ajeno._rfc} y NO corresponde al proveedor seleccionado. Quítalo o corrige el proveedor.`, 'error');
      return;
    }
    // PDF obligatorio excepto nómina, caja chica y reembolso de gastos (llevan PDF por concepto)
    if (!esNomina && !esCajaChica && !esReembolsoGastos && !adjuntos.some(f => f.name.toLowerCase().endsWith('.pdf'))) {
      toast_('Adjunta el PDF de la factura o documento del gasto — es obligatorio para crear la solicitud.', 'error');
      return;
    }
    setLoading(true);
    try {
      const sol = await api.post('/solicitudes', { ...buildPayload(), cuentaPorConfirmar: variasCuentas && eleccionCuenta === POR_CONFIRMAR });
      await subirAdjuntos(sol._id);
      toast_(`Solicitud ${sol.folio} guardada`);
      if (onCreated) onCreated();
      resetCampos();
    } catch (e) { toast_(e.message, 'error'); }
    finally { setLoading(false); }
  };

  const handlePDF = async () => {
    if (!validate()) { toast_('Completa los campos requeridos', 'error'); return; }
    const ajenoPdf = cfdiAjeno();
    if (ajenoPdf) {
      toast_(`El CFDI ${ajenoPdf.folioCfdi || ajenoPdf.notasCredito} es de ${ajenoPdf._rfc} y NO corresponde al proveedor seleccionado. Quítalo o corrige el proveedor.`, 'error');
      return;
    }
    setLoading(true);
    try {
      const sol = await api.post('/solicitudes', { ...buildPayload(), cuentaPorConfirmar: variasCuentas && eleccionCuenta === POR_CONFIRMAR });
      await subirAdjuntos(sol._id);
      const blob = await api.downloadPdf(`/solicitudes/${sol._id}/pdf`);
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement('a'), { href: url, download: `Solicitud_${sol.folio}.pdf` }).click();
      URL.revokeObjectURL(url);
      toast_(`Solicitud ${sol.folio} generada`);
      if (onCreated) onCreated();
    } catch (e) { toast_(e.message, 'error'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />

      <PageHeader
        icon={cfg.icon}
        title={cfg.titulo}
        subtitle={user?.nombre}
        actions={
          <>
            <button onClick={() => setPlantillaModal(true)}
              style={{ ...css.btn('secundario'), fontSize: 12 }}><ConIcono nombre="carpeta">Plantillas</ConIcono></button>
            <button onClick={() => setGuardarPlantillaModal(true)}
              style={{ ...css.btn('secundario'), fontSize: 12 }}><ConIcono nombre="guardar">Guardar plantilla</ConIcono></button>
            <button onClick={() => { setModoFacturas(false); setErrors({}); setForm(f => ({ ...f, empresa: '', sucursal: '', centroCostos: '', direccion: '', proveedor: '', banco: '', clabe: '', cuenta: '', folioCfdi: '', notasCredito: '', monto: '', facturas: [], motivoPago: '', observaciones: '', impuestos: [], impuestosImportes: {} })); }}
              style={{ ...css.btn('secundario'), fontSize: 12 }}>Limpiar</button>
            <button onClick={handleGuardar} disabled={loading}
              style={{ ...css.btn('secundario'), fontSize: 12 }}>
              {loading ? '...' : <ConIcono nombre="guardar">Guardar</ConIcono>}
            </button>
            <button onClick={handlePDF} disabled={loading}
              style={{ ...css.btn('secundario'), fontSize: 12, minWidth: 130 }}>
              {loading ? 'Generando...' : <ConIcono nombre="descarga">Guardar y PDF</ConIcono>}
            </button>
          </>
        }
      />

      <div className="ui-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 300px', gap: 20 }}>
        {/* Formulario */}
        <div style={{ ...css.card, padding: 28 }}>
          <SectionHeader>Datos Generales</SectionHeader>
          <Grid cols={2} gap={14} style={{ marginBottom: 14 }}>
            <div>
              <Label required>Compañía</Label>
              <select value={form.empresa} onChange={e => handleEmpresa(e.target.value)} style={css.select(errors.empresa)}>
                <option value="">Seleccionar empresa...</option>
                {empresas.map(e => <option key={e._id} value={e.nombre}>{e.nombre}</option>)}
              </select>
            </div>
            <div>
              <Label required>Fecha</Label>
              <input type="date" value={form.fechaSolicitud} readOnly style={{ ...css.input(false), ...css.inputRO }} />
            </div>
          </Grid>
          <Grid cols={3} gap={14} style={{ marginBottom: 14 }}>
            <div>
              <Label required>Sucursal</Label>
              <select value={form.sucursal} onChange={e => handleSucursal(e.target.value)} disabled={!form.empresa} style={css.select(errors.sucursal)}>
                <option value="">Seleccionar...</option>
                {sucursalesFiltradas.map(s => <option key={s._id} value={s.nombre}>{s.nombre}</option>)}
              </select>
            </div>
            <div>
              <Label required>Centro de Costos</Label>
              <select value={form.centroCostos} onChange={e => handleCentro(e.target.value)} disabled={!form.sucursal} style={css.select(errors.centroCostos)}>
                <option value="">Seleccionar...</option>
                {(() => {
                  const secs = [...new Set(centros.map(c => (c.seccion || c.direccion || '').trim()).filter(Boolean))];
                  const sinSec = centros.filter(c => !(c.seccion || c.direccion || '').trim());
                  return [
                    ...secs.map(sec => (
                      <optgroup key={sec} label={sec}>
                        {centros.filter(c => (c.seccion || c.direccion || '').trim() === sec).map(c => (
                          <option key={c._id} value={c.nombre}>{c.nombre}</option>
                        ))}
                      </optgroup>
                    )),
                    ...sinSec.map(c => <option key={c._id} value={c.nombre}>{c.nombre}</option>),
                  ];
                })()}
              </select>
            </div>
            <div>
              <Label>Dirección</Label>
              <select value={form.direccion} onChange={e => set('direccion', e.target.value)} style={css.select(false)}>
                <option value="">Seleccionar...</option>
                {['DIRECCION GENERAL','DIRECCION COMERCIAL','DIRECCION COMPRAS','DIRECCION JURIDICO RH',
                  'DIRECCION FINANZAS','DIRECCION UNE CRISTALERIA','DIRECCION UNE MEDICAL STORE',
                  'DIRECCION LOGISTICA','DIRECCION GOLDEN','DIRECCION PATRIMONIAL','DIRECCION FONTASTIC',
                ].map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </Grid>

          <SectionHeader>Proveedor / Beneficiario</SectionHeader>
          <div style={{ marginBottom: 14 }}>
            <Label required>Buscar Proveedor</Label>
            <ProveedorSearch value={form.proveedor} onSelect={handleProveedor} endpoint={cfg.proveedores || '/proveedores'} />
            {errors.proveedor && <div style={{ color: 'var(--danger)', fontSize: 11.5, marginTop: 3 }}>Campo requerido</div>}
          </div>
          {variasCuentas && (
            <SelectorCuentaProveedor cuentas={cuentasProv.cuentas} value={eleccionCuenta} error={errors.cuentaProveedor}
              onChange={v => (v === POR_CONFIRMAR
                ? (setEleccionCuenta(v), setForm(f => ({ ...f, banco: '', clabe: '', cuenta: '' })), errors.cuentaProveedor && setErrors(e => ({ ...e, cuentaProveedor: false })))
                : elegirCuenta(v))} />
          )}
          <Grid cols={3} gap={14} style={{ marginBottom: 14 }}>
            <div><Label>Banco</Label><input value={form.banco} readOnly style={{ ...css.input(false), ...css.inputRO }} /></div>
            <div>
              <Label>CLABE</Label>
              <input value={form.clabe} readOnly style={{ ...css.input(false), ...css.inputRO, fontFamily: 'var(--digit-font)', fontSize: 11.5 }} />
              {form.clabe && (() => {
                const r = validarCLABE(form.clabe);
                if (!r) return null;
                return (
                  <div style={{ fontSize: 11.5, marginTop: 3, fontWeight: 600, color: r.valida ? 'var(--ok)' : 'var(--danger)' }}>
                    <ConIcono nombre={r.valida ? 'check' : 'cerrar'} size={13}>{r.mensaje}</ConIcono>
                  </div>
                );
              })()}
            </div>
            <div><Label>No. Cuenta</Label><input value={form.cuenta} readOnly style={{ ...css.input(false), ...css.inputRO, fontFamily: 'var(--digit-font)', fontSize: 11.5 }} /></div>
          </Grid>

          {/* CFDI / Facturas — no aplica a nómina, débito, caja chica ni reembolso de gastos */}
          {!esNomina && !esDebito && !esCajaChica && !esReembolsoGastos && !esImportacion && !esContribuciones && (<>
          {/* Encabezado y botones en una línea; los avisos y archivos bajan a su propia línea */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', columnGap: 16, marginBottom: 8 }}>
            <div style={{ flex: '1 1 160px' }}><SectionHeader>{modoFacturas ? 'Facturas / CFDI' : 'CFDI'}</SectionHeader></div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <input ref={xmlInputRef} type="file" accept=".xml,text/xml,application/xml" multiple
                style={{ display: 'none' }} onChange={e => handleImportXML(e.target.files)} />
              <button onClick={() => xmlInputRef.current?.click()} disabled={importando} style={{ ...css.btn('secundario'), padding: '4px 12px', fontSize: 11.5, cursor: importando ? 'wait' : 'pointer' }} title="Lee el XML del SAT y autocompleta folio, monto e impuestos">
                {importando ? 'Leyendo...' : <ConIcono nombre="codigo" size={13}>Importar XML</ConIcono>}
              </button>
              <input ref={pdfInputRef} type="file" accept=".pdf,application/pdf" multiple
                style={{ display: 'none' }} onChange={e => handleAdjuntarPDF(e.target.files)} />
              <button onClick={() => pdfInputRef.current?.click()} style={{ ...css.btn('secundario'), padding: '4px 12px', fontSize: 11.5 }} title="Adjunta el PDF de la factura: quedará en el expediente de la solicitud (obligatorio)">
                <ConIcono nombre="clip" size={13}>Adjuntar PDF <span style={{ color: 'var(--danger)', fontWeight: 800 }}>*</span></ConIcono>
              </button>
              <button onClick={toggleModo} style={{
                padding: '4px 12px', borderRadius: 6, fontSize: 11.5, fontWeight: 600,
                border: `1.5px solid ${modoFacturas ? 'var(--plasma-line)' : 'var(--hair)'}`,
                background: modoFacturas ? 'var(--plasma-dim)' : 'var(--panel)',
                color: modoFacturas ? 'var(--plasma)' : 'var(--glass)',
                cursor: 'pointer', fontFamily: 'Inter, sans-serif',
              }}>
                {modoFacturas ? <ConIcono nombre="cerrar" size={13}>Monto simple</ConIcono> : <ConIcono nombre="mas" size={13}>Múltiples facturas</ConIcono>}
              </button>
            </div>
            {/* El PDF del gasto es obligatorio: recordatorio visible hasta que lo adjunten */}
            {!adjuntos.some(f => f.name.toLowerCase().endsWith('.pdf')) && (
              <div style={{ flexBasis: '100%', fontSize: 12, color: 'var(--danger)', marginTop: 6, fontWeight: 600 }}>
                <Icono nombre="alerta" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />Adjunta el PDF de la factura o documento del gasto — es obligatorio para crear la solicitud.
              </div>
            )}
            {/* Archivos que se adjuntarán al expediente al crear la solicitud */}
            {adjuntos.length > 0 && (
              <div style={{ flexBasis: '100%', display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, alignItems: 'center' }}>
                {adjuntos.map((f, i) => (
                  <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 6, fontSize: 11.5, background: 'var(--panel)', border: '1px solid var(--hair)', color: 'var(--ink-soft)', maxWidth: 240 }}>
                    <Icono nombre={f.name.toLowerCase().endsWith('.xml') ? 'codigo' : 'documento'} size={13} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                    <button onClick={() => setAdjuntos(a => a.filter((_, j) => j !== i))} title="Quitar archivo" aria-label="Quitar archivo"
                      style={{ border: 'none', background: 'transparent', color: 'var(--danger)', cursor: 'pointer', fontSize: 12, padding: 0, flexShrink: 0 }}><Icono nombre="cerrar" size={13} /></button>
                  </span>
                ))}
                <span style={{ fontSize: 11.5, color: 'var(--glass)' }}>· se guardarán en el expediente al crear la solicitud</span>
              </div>
            )}
          </div>

          {modoFacturas ? (
            <div style={{ marginBottom: 14 }}>
              <FacturasTable facturas={form.facturas} onChange={handleFacturasChange} hasError={errors.facturas} rfcProveedor={form.rfcProveedor} />
            </div>
          ) : (
            <Grid cols={2} gap={14} style={{ marginBottom: 14 }}>
              <div>
                <Label>No. Folio(s) CFDI</Label>
                <input value={form.folioCfdi} onChange={e => {
                  set('folioCfdi', e.target.value);
                  clearTimeout(cfdiTimer.current);
                  cfdiTimer.current = setTimeout(() => checkCfdi(e.target.value), 400);
                }} placeholder="CFDI-001" style={css.input(!!cfdiDuplicado)} />
                {cfdiDuplicado && (
                  <div style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 3, fontWeight: 600 }}>
                    <Icono nombre="alerta" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />Ya existe en {cfdiDuplicado.folio} ({cfdiDuplicado.proveedor})
                  </div>
                )}
              </div>
              <div><Label>Notas de Crédito</Label><input value={form.notasCredito} onChange={e => set('notasCredito', e.target.value)} style={css.input(false)} /></div>
            </Grid>
          )}
          </>)}

          <SectionHeader>Datos de Pago</SectionHeader>
          {(modoFacturas || esNomina || esDebito || esCajaChica || esReembolsoGastos || esImportacion || esContribuciones) ? (
            <Grid cols={3} gap={14} style={{ marginBottom: 14 }}>
              <div>
                <Label>Moneda</Label>
                <select value={form.moneda} onChange={e => set('moneda', e.target.value)} style={css.select(false)}>
                  <option value="MXN">MXN</option><option value="USD">USD</option><option value="EUR">EUR</option>
                </select>
              </div>
              <div>
                <Label>Forma de Pago</Label>
                <select value={form.formaPago} onChange={e => set('formaPago', e.target.value)} style={css.select(false)}>
                  {['TRANSFERENCIA ELECTRÓNICA', 'CHEQUE', 'EFECTIVO', 'CONTADO', 'REFERENCIA BANCARIA'].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <Label>Tipo</Label>
                <select value={form.tipoPago} onChange={e => set('tipoPago', e.target.value)} style={css.select(false)}>
                  <option value="Programado">Programado</option>
                  <option value="No Programado">No Programado</option>
                </select>
              </div>
            </Grid>
          ) : (
            <Grid cols={4} gap={14} style={{ marginBottom: 14 }}>
              <div>
                <Label required>Monto ($)</Label>
                <MontoInput value={form.monto} onChange={v => set('monto', v)} err={errors.monto} />
                {errors.monto && <div style={{ color: 'var(--danger)', fontSize: 11.5, marginTop: 3 }}>Monto inválido</div>}
              </div>
              <div>
                <Label>Moneda</Label>
                <select value={form.moneda} onChange={e => set('moneda', e.target.value)} style={css.select(false)}>
                  <option value="MXN">MXN</option><option value="USD">USD</option><option value="EUR">EUR</option>
                </select>
              </div>
              <div>
                <Label>Forma de Pago</Label>
                <select value={form.formaPago} onChange={e => set('formaPago', e.target.value)} style={css.select(false)}>
                  {['TRANSFERENCIA ELECTRÓNICA', 'CHEQUE', 'EFECTIVO', 'CONTADO', 'REFERENCIA BANCARIA'].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <Label>Tipo</Label>
                <select value={form.tipoPago} onChange={e => set('tipoPago', e.target.value)} style={css.select(false)}>
                  <option value="Programado">Programado</option>
                  <option value="No Programado">No Programado</option>
                </select>
              </div>
            </Grid>
          )}
          {esCajaChica ? (
            <div style={{ marginBottom: 14 }}>
              <Label required>Conceptos de Caja Chica (motivo + comprobante por concepto)</Label>
              {/* Inputs ocultos por concepto (XML y PDF) */}
              {conceptosCC.map((_, i) => (
                <React.Fragment key={i}>
                  <input ref={el => { ccXmlRefs.current[i] = el; }} type="file" accept=".xml,text/xml,application/xml" style={{ display: 'none' }} onChange={e => handleImportXMLCC(i, e.target.files)} />
                  <input ref={el => { ccPdfRefs.current[i] = el; }} type="file" accept=".pdf,application/pdf" style={{ display: 'none' }} onChange={e => handleAdjuntarPDFCC(i, e.target.files)} />
                </React.Fragment>
              ))}
              <ConceptosCajaChica
                conceptos={conceptosCC} onChange={setConceptosCC}
                motivos={motivosCatalogo} hasError={errors.conceptosCC}
                importandoIdx={importandoCCIdx}
                onImportXml={(i) => ccXmlRefs.current[i]?.click()}
                onAdjuntarPdf={(i) => ccPdfRefs.current[i]?.click()}
              />
            </div>
          ) : esReembolsoGastos ? (
            <div style={{ marginBottom: 14 }}>
              <Label required>Conceptos de Reembolso de Gastos (motivo + comprobante por concepto)</Label>
              {/* Inputs ocultos por concepto (XML y PDF) */}
              {conceptosRG.map((_, i) => (
                <React.Fragment key={i}>
                  <input ref={el => { rgXmlRefs.current[i] = el; }} type="file" accept=".xml,text/xml,application/xml" style={{ display: 'none' }} onChange={e => handleImportXMLRG(i, e.target.files)} />
                  <input ref={el => { rgPdfRefs.current[i] = el; }} type="file" accept=".pdf,application/pdf" style={{ display: 'none' }} onChange={e => handleAdjuntarPDFRG(i, e.target.files)} />
                </React.Fragment>
              ))}
              <ConceptosCajaChica
                conceptos={conceptosRG} onChange={setConceptosRG}
                motivos={motivosCatalogo} hasError={errors.conceptosRG}
                importandoIdx={importandoRGIdx}
                onImportXml={(i) => rgXmlRefs.current[i]?.click()}
                onAdjuntarPdf={(i) => rgPdfRefs.current[i]?.click()}
              />
            </div>
          ) : esDebito ? (
            <div style={{ marginBottom: 14 }}>
              <Label required>Conceptos del Débito (motivo → cuenta contable)</Label>
              <input ref={debitoXmlRef} type="file" accept=".xml,text/xml,application/xml" style={{ display: 'none' }} onChange={e => handleImportXMLDebito(e.target.files)} />
              <ConceptosDebito conceptos={conceptosDeb} onChange={setConceptosDeb} motivos={motivosCatalogo} hasError={errors.conceptosDeb}
                importando={importandoDeb} onImportar={() => debitoXmlRef.current?.click()} />
            </div>
          ) : esNomina ? (
            <div style={{ marginBottom: 14 }}>
              <Label required>Motivos de Pago (suman o restan al neto)</Label>
              <ConceptosNomina conceptos={conceptos} onChange={setConceptos} motivos={motivosCatalogo} hasError={errors.conceptos} />
            </div>
          ) : esImportacion ? (
            <>
              <SectionHeader>Datos de la Importación</SectionHeader>
              <Grid cols={2} gap={14} style={{ marginBottom: 14 }}>
                <div>
                  <Label required>Referencia Aduanal</Label>
                  <input value={form.referenciaAduanal} onChange={e => set('referenciaAduanal', e.target.value)}
                    placeholder="Ej: 26 43 3512 6000123" style={css.input(errors.referenciaAduanal)} />
                  {errors.referenciaAduanal && <div style={{ color: 'var(--danger)', fontSize: 11.5, marginTop: 3 }}>Campo requerido</div>}
                </div>
                <div>
                  <Label>Número de Contenedor</Label>
                  <input value={form.numeroContenedor} onChange={e => set('numeroContenedor', e.target.value)}
                    placeholder="Ej: MSCU 123456-7" style={css.input(false)} />
                </div>
              </Grid>
              <div style={{ marginBottom: 14 }}>
                <Label required>Motivos de Pago (conceptos del complemento)</Label>
                <input ref={impXmlRef} type="file" accept=".xml,text/xml,application/xml" style={{ display: 'none' }} onChange={e => handleImportXMLImportacion(e.target.files)} />
                {/* Un input oculto por concepto: cada renglón adjunta su propia factura */}
                {conceptosImp.map((_, i) => (
                  <input key={i} ref={el => { impConceptoXmlRefs.current[i] = el; }} type="file"
                    accept=".xml,text/xml,application/xml" style={{ display: 'none' }}
                    onChange={e => handleImportXMLImpConcepto(i, e.target.files)} />
                ))}
                <ConceptosPorMotivo conceptos={conceptosImp} onChange={setConceptosImp} motivos={motivosCatalogo}
                  hasError={errors.conceptosImp} impuestosCfdi={form.impuestosImportes || {}}
                  importando={importandoImp} onImportar={() => impXmlRef.current?.click()}
                  importandoIdx={importandoImpIdx}
                  onImportarConcepto={(i) => impConceptoXmlRefs.current[i]?.click()} />
              </div>
            </>
          ) : esContribuciones ? (
            <div style={{ marginBottom: 14 }}>
              <Label required>Motivos de Contribución</Label>
              <div style={{ fontSize: 11.5, color: 'var(--glass)', marginBottom: 6 }}>
                Un pago puede cubrir varios impuestos (IMSS, INFONAVIT, ISR retenido…). Cada uno se
                clasifica en su propia cuenta contable y el monto de la solicitud es la suma.
              </div>
              <ConceptosPorMotivo conceptos={conceptosContrib} onChange={setConceptosContrib}
                motivos={motivosCatalogo} hasError={errors.conceptosContrib}
                etiquetaTotal="Total a pagar" />
            </div>
          ) : (
            <div style={{ marginBottom: 14 }}>
              <Label required>Motivo de Pago</Label>
              <MotivoSearch value={form.motivoPago} onChange={v => set('motivoPago', v)} hasError={errors.motivoPago} motivos={motivosVisibles} />
              {errors.motivoPago && <div style={{ color: 'var(--danger)', fontSize: 11.5, marginTop: 3 }}>Campo requerido</div>}
            </div>
          )}
          {(esNomina || esDebito || esImportacion) && (
            <div style={{ marginBottom: 14 }}>
              <Label>Adjuntar {adjuntosConXml ? 'PDF y XML' : 'PDF'}{esDebito ? ' (obligatorio)' : ''}{esImportacion ? ' (el PDF es obligatorio)' : ''}</Label>
              <input ref={pdfInputRef} type="file" multiple style={{ display: 'none' }}
                accept={adjuntosConXml ? '.pdf,.xml,application/pdf,text/xml,application/xml' : '.pdf,application/pdf'}
                onChange={e => handleAdjuntarPDF(e.target.files)} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={() => pdfInputRef.current?.click()} style={{ ...css.btn('secundario'), padding: '6px 14px', fontSize: 12 }}><ConIcono nombre="clip">Adjuntar {adjuntosConXml ? 'PDF o XML' : 'PDF'}</ConIcono></button>
                {adjuntos.map((f, i) => (
                  <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 6, fontSize: 11.5, background: 'var(--panel)', border: '1px solid var(--hair)', color: 'var(--ink-soft)', maxWidth: 240 }}>
                    <Icono nombre={f.name.toLowerCase().endsWith('.xml') ? 'codigo' : 'documento'} size={13} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                    <button onClick={() => setAdjuntos(a => a.filter((_, j) => j !== i))} title="Quitar archivo" aria-label="Quitar archivo" style={{ border: 'none', background: 'transparent', color: 'var(--danger)', cursor: 'pointer', fontSize: 12, padding: 0, flexShrink: 0 }}><Icono nombre="cerrar" size={13} /></button>
                  </span>
                ))}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 4 }}>
                {esDebito
                  ? 'Adjunta el PDF de la factura (obligatorio). El XML importado también queda en el expediente.'
                  : esImportacion
                    ? 'El PDF se guarda en el expediente. Si sueltas aquí el XML del CFDI, además se lee y llena los conceptos, igual que el botón "Importar XML" de arriba.'
                    : 'Opcional. Se guardará en el expediente al crear la solicitud.'}
              </div>
            </div>
          )}
          <div>
            <Label required>Concepto del Pago</Label>
            <textarea value={form.observaciones} onChange={e => set('observaciones', e.target.value)} rows={3}
              placeholder="Describe con detalle qué se está pagando (mínimo 50 caracteres)..."
              style={{ ...css.input(errors.observaciones), resize: 'vertical' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 3 }}>
              {errors.observaciones
                ? <span style={{ color: 'var(--danger)', fontSize: 11.5 }}>Mínimo 50 caracteres — describe el concepto con más detalle</span>
                : <span />}
              <span style={{ fontSize: 11.5, fontFamily: 'var(--digit-font)', color: form.observaciones.trim().length >= 50 ? 'var(--ok)' : 'var(--glass)' }}>
                {form.observaciones.trim().length}/50
              </span>
            </div>
          </div>
        </div>

        {/* Panel resumen */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ background: 'var(--panel)', borderRadius: 14, overflow: 'hidden', border: '1px solid var(--hair)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)' }}>
            <div style={{ background: 'var(--panel-hi)', borderBottom: '1px solid var(--mesh-soft)', padding: '16px 18px' }}>
              <div style={{ fontSize: 11.5, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Compañía</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)', marginTop: 3 }}>{form.empresa || '—'}</div>
            </div>
            <div style={{ padding: '14px 18px' }}>
              {[['Proveedor', form.proveedor], ['Sucursal', form.sucursal], ['Centro Costos', form.centroCostos], ['Banco', form.banco]].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--hair-soft)' }}>
                  <span style={{ fontSize: 11.5, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{l}</span>
                  <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink)', maxWidth: 140, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v || '—'}</span>
                </div>
              ))}
              {totalBase > 0 && (() => {
                const { lineas, total } = calcImpuestos(totalBase, form.impuestos, form.impuestosImportes);
                const fmt = n => `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
                return (
                  <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--hair)' }}>
                    {modoFacturas && (
                      <div style={{ fontSize: 11.5, color: 'var(--glass)', marginBottom: 4 }}>
                        {form.facturas.length} factura{form.facturas.length !== 1 ? 's' : ''}
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                      <span style={{ fontSize: 11.5, color: 'var(--glass)', textTransform: 'uppercase' }}>Subtotal</span>
                      <span style={{ fontSize: 12, color: 'var(--ink-soft)', fontFamily: 'var(--digit-font)' }}>{fmt(totalBase)}</span>
                    </div>
                    {lineas.map(l => (
                      <div key={l.clave} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <span style={{ fontSize: 11.5, color: l.esRetencion ? 'var(--danger)' : 'var(--ok)' }}>{l.label}</span>
                        <span style={{ fontSize: 11.5, color: l.esRetencion ? 'var(--danger)' : 'var(--ok)', fontWeight: 600, fontFamily: 'var(--digit-font)' }}>
                          {l.esRetencion ? `-${fmt(l.valor)}` : `+${fmt(l.valor)}`}
                        </span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--hair-soft)' }}>
                      <span style={{ fontSize: 11.5, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Neto a Pagar</span>
                      <div>
                        <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--plasma)', transition: 'color 0.3s', fontFamily: 'var(--digit-font)' }}>{fmt(total)}</span>
                        <span style={{ fontSize: 11.5, color: 'var(--glass)', marginLeft: 4 }}>{form.moneda}</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          <button onClick={handlePDF} disabled={loading}
            style={{ ...css.btn('primario'), padding: '13px', fontSize: 13.5, width: '100%' }}>
            {loading ? 'Generando...' : <ConIcono nombre="descarga">Guardar y PDF</ConIcono>}
          </button>
        </div>
      </div>

      {/* Modal: cargar plantilla */}
      <Modal open={plantillaModal} onClose={() => setPlantillaModal(false)} title="Plantillas guardadas" width={460}>
        {plantillas.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--glass)', padding: '24px 0', fontSize: 13 }}>
            No tienes plantillas guardadas.<br />Rellena el formulario y usa "Guardar plantilla".
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {plantillas.map(p => (
              <div key={p._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--hair-soft)', borderRadius: 8, border: '1px solid var(--hair)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>{p.nombre}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--glass)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {[p.proveedor, p.empresa, p.motivoPago].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <button onClick={() => aplicarPlantilla(p)} style={{ ...css.btn('secundario'), fontSize: 12, padding: '5px 12px', flexShrink: 0 }}>Aplicar</button>
                <button aria-label="Eliminar plantilla" title="Eliminar plantilla" onClick={() => eliminarPlantilla(p._id)} style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--danger-line)', background: 'var(--danger-bg)', color: 'var(--danger)', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}><Icono nombre="papelera" size={15} /></button>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Modal: guardar plantilla */}
      <Modal open={guardarPlantillaModal} onClose={() => { setGuardarPlantillaModal(false); setNombrePlantilla(''); }} title="Guardar como plantilla" width={380}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, color: 'var(--glass)' }}>Se guardarán los datos del proveedor, empresa, motivo y configuración de pago (sin montos ni folios CFDI).</div>
          <input autoFocus value={nombrePlantilla} onChange={e => setNombrePlantilla(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && guardarComoPlantilla()}
            placeholder="Nombre de la plantilla..." style={css.input(!nombrePlantilla.trim())} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => { setGuardarPlantillaModal(false); setNombrePlantilla(''); }} style={{ ...css.btn('secundario'), fontSize: 13 }}>Cancelar</button>
            <button onClick={guardarComoPlantilla} disabled={!nombrePlantilla.trim()} style={{ ...css.btn('primario'), fontSize: 13 }}>Guardar</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
